/**
 * Zustand store for AG-UI chat functionality
 * Manages status updates, streaming, tool execution, workflow integration, and change proposals
 */

import { create } from 'zustand';
import type { AguiMessage, ToolExecutionRequest, AzureContext, AguiEvent, StageEventSnapshot, StageProgressEvent, PipelineStageId, AssistantTurnMetadata, ClarificationSnapshot } from '@/types/agui';
import type { WorkflowProposal } from '@/types/proposal';
import { streamAguiMessage, postToolResult, type MessageHistory, type ScopedNode } from '@/services/aguiStreamClient';
import { getValueAtPath, setValueAtPath, resolveNodePath, computeJsonDiff, validateProposedJson } from '@/utils/json';
import {
  AzureToolExecutor,
  patchHttpActionsInAgentTools,
  setOnToolDefinitionsReadyCallback,
  getLastDiscoveredToolDefinitions,
} from '@/services/azureToolExecutor';
import { useWorkflowStore } from '@/stores/workflowStore';
import { composeWorkflowKey, type WorkflowIdentity } from '@/stores/workflowViewPrefsStore';
import { useDraftModeStore } from '@/stores/draftModeStore';
import { queryClient } from '@/providers/QueryProvider';
import { fetchConnectionsJson, fetchConnectionsDraftJson } from '@/services/logicAppsApi';
import { useConnectionStore } from '@/stores/connectionStore';
import {
  analyzeConnectionRequirements,
  applyConnectionAssignments,
  generateRequiredConnectionsMessage,
} from '@/services/connectionMatchingService';
import type { ConnectionMatchingResult } from '@/types/connectionProposal';
import * as chatHistoryClient from '@/services/chatHistoryClient';
import type { ChatHistoryFetchOptions } from '@/services/chatHistoryClient';
import type { FeedbackSentiment } from '@/services/workflowAssistantFeedback';
import { telemetryService } from '@/services/telemetryService';
import { formatStructuredErrorMessage } from '@/utils/monitoring';
import {
  validateForEachConstraints,
  FOREACH_ACTION_LIMIT_TITLE,
} from '@/utils/validation';
import { useNotificationStore } from '@/stores/notificationStore';
import { listKnowledgeHubs } from '@/services/knowledgeHubService';
import { useAppStore } from '@/stores/appStore';

/** Append a ConnectionSetupCard message when there are unresolved connections. */
function appendConnectionSetupMessage(
  set: (fn: (state: AgentChatAguiState) => Partial<AgentChatAguiState>) => void,
  connectionMatching: ConnectionMatchingResult | undefined
): void {
  if (!connectionMatching) return;
  const hasUnresolved =
    connectionMatching.noConnectionAvailable.length > 0 ||
    connectionMatching.needsApproval.some(p => p.status === 'pending');
  if (!hasUnresolved) return;
  set((state) => ({
    chatMessages: [...state.chatMessages, {
      id: `chat-connections-${Date.now()}`,
      role: 'assistant' as const,
      content: '',
      timestamp: Date.now(),
      connectionSetup: connectionMatching,
    }]
  }));
}

/**
 * Sanitize error messages to remove internal implementation details
 * that shouldn't be shown to users (class names, stack traces, JSON, etc.)
 */
function sanitizeErrorMessage(error: string | undefined | null): string {
  if (!error) return 'An unexpected error occurred';

  // Try to parse JSON error responses from the API
  if (error.includes('"type":"error"') || error.startsWith('{')) {
    try {
      // Extract JSON from the error string (might be prefixed with "Error: ")
      const jsonMatch = error.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        // Claude API error format: { type: "error", error: { type: "...", message: "..." } }
        if (parsed.error?.message) {
          // Simplify common API error messages
          const msg = parsed.error.message;
          if (msg.includes('non-empty content')) {
            return 'Unable to process the request. Please try rephrasing your message.';
          }
          if (msg.includes('rate_limit') || msg.includes('overloaded')) {
            return 'The service is temporarily busy. Please try again in a moment.';
          }
          // Return a simplified version for other API errors
          return 'Request failed. Please try again.';
        }
      }
    } catch {
      // JSON parsing failed, continue with other sanitization
    }
  }

  // All infrastructure / internal error patterns — users cannot act on these,
  // so surface a single generic message with a retry suggestion.
  const internalMessage = 'Something went wrong on our end. Please try again — if the issue persists, contact your administrator.';

  // Check for HTTP error patterns
  if (error.includes('HTTP 400') || error.includes('Bad Request')) {
    return internalMessage;
  }
  if (error.includes('HTTP 401') || error.includes('Unauthorized') || error.includes('Authorization error') || error.includes('status: 401')) {
    return internalMessage;
  }
  if (error.includes('HTTP 429') || error.includes('Too Many Requests')) {
    return 'Too many requests. Please wait a moment and try again.';
  }
  if (error.includes('HTTP 5')) {
    return internalMessage;
  }

  // Check for common internal error patterns
  if (error.includes('AgUiConverter') || error.includes('ClaudeAzureApi')) {
    return internalMessage;
  }
  if (error.includes('DeploymentNotFound') || error.includes('deployment for this resource does not exist')) {
    return internalMessage;
  }
  if (error.includes('AgentLoopChatCompletionFailed')) {
    return internalMessage;
  }
  if (error.includes('type initializer')) {
    return internalMessage;
  }
  if (error.includes('RegexParseException')) {
    return internalMessage;
  }

  // Remove any namespace prefixes (e.g., "ClaudeAzureApi.Services.ClassName")
  const sanitized = error.replace(/[A-Za-z]+(\.[A-Za-z]+)+/g, '').trim();

  // If the sanitized message is too short or empty, use a generic message
  if (sanitized.length < 10) {
    return internalMessage;
  }

  // If the sanitized message is too long (likely contains JSON or stack trace), use generic
  if (sanitized.length > 100) {
    return internalMessage;
  }

  return sanitized;
}

// Visible chat message for display
interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
  /**
   * Optional pipeline-stage breakdown captured during the assistant turn that
   * produced this message. Persists to chat history (as JSON) so a reloaded
   * chat shows the same Plan → Build → Validate cards the user saw live.
   * Always undefined on user messages.
   */
  stages?: StageEventSnapshot[];
  /**
   * Optional clarification card attached to this assistant turn. When set,
   * the chat renderer shows a ClarificationCard instead of (or in addition
   * to) the message content. Persists to chat history so reloaded chats
   * show the same card read-only with the user's answer highlighted.
   */
  clarification?: ClarificationSnapshot;
  /**
   * Optional connection setup result attached after workflow apply.
   * When set, the chat renderer shows a ConnectionSetupCard listing
   * unresolved connection requirements needing user action.
   */
  connectionSetup?: ConnectionMatchingResult;
  /**
   * Thumbs up/down feedback the user submitted for this assistant turn. Set
   * optimistically on submit; persisted to (and restored from) chat history so
   * a reloaded chat shows the same rating. Always undefined on user messages.
   */
  feedback?: FeedbackSentiment;
}

/**
 * Index of the latest assistant message — the one the feedback control rates.
 * Returns -1 if there are no assistant messages. We target the newest assistant
 * row directly (regardless of content) so the thumb always lands on the reply the
 * user just saw, rather than skipping back to an earlier (possibly already-rated)
 * turn.
 */
function latestFeedbackAnchorIndex(messages: { role: string }[]): number {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i]!.role === 'assistant') return i;
  }
  return -1;
}

// Edit scope for scoped action/trigger editing
export interface EditScope {
  type: 'workflow' | 'node';
  name?: string;           // Node name when scoped (single node) — used for display/apply
  /** JSON path to the node in the workflow definition (e.g. "definition.actions.My_If.actions.Send_email") */
  jsonPath?: string;
  /** Node names when multi-node scoped (type will be 'workflow' since multi applies as full workflow) */
  nodeNames?: string[];
}

/**
 * Sticky edit scope - maintained until user explicitly exits
 * Used to persist edit mode even when clicking away from nodes
 */
export interface StickyEditScope {
  type: 'workflow' | 'single' | 'multi';
  /** Node names being edited (for single/multi) */
  nodeNames?: string[];
  /** JSON path to the node in the workflow definition (for single node) */
  jsonPath?: string;
  /** Connector color for the scope banner */
  color?: string;
}

/** Source identifier for AI entry points — used for telemetry */
export type CopilotEntryPointSource =
  | 'picker-no-results'
  | 'node-editor-header'
  | 'command-palette'
  | 'empty-params'
  | 'template-preview'
  | 'workflow-list-hero'
  | 'workflow-list-toolbar';

/** Queued prompt from any entry point, consumed by WorkflowDesignerWithCode */
export interface PendingPrompt {
  content: string;
  scope: StickyEditScope;
  source: CopilotEntryPointSource;
  /** Hidden context prepended to the prompt but not shown in chat UI */
  systemContext?: string | undefined;
}

interface AgentChatAguiState {
  // UI state
  isOpen: boolean;
  sidebarWidth: number;

  // Visible chat messages (accumulated history)
  chatMessages: ChatMessage[];

  // Current status (shown during processing, replaced not accumulated)
  currentStatus: string | null;

  /**
   * Per-stage breakdown for the currently-in-flight assistant turn. Keyed by
   * stage id so each new `status_update` for a given stage replaces the prior
   * entry (the backend ships the full snapshot of the stage on every emit).
   * Cleared when the turn finishes; snapshotted onto the assistant ChatMessage's
   * `stages` field so completed turns persist their breakdown.
   */
  currentTurnStages: Partial<Record<PipelineStageId, StageEventSnapshot>>;

  /**
   * When set, the pipeline has asked the user a question and is waiting.
   * The main ChatInput is disabled, and the chat sidebar renders a
   * ClarificationCard with the question + options. Cleared when the user
   * answers (which sends their reply through the normal sendMessage path).
   */
  pendingClarification: ClarificationSnapshot | null;

  // Internal message history (for API context, not displayed)
  messageHistory: AguiMessage[];

  // Streaming
  isStreaming: boolean;

  // Response is eligible for feedback
  feedbackEligible: boolean;

  // Bumped whenever the user submits turn feedback, so the sidebar can persist
  // the chat promptly (feedback doesn't change message count → the count-based
  // auto-save won't fire on its own).
  feedbackSaveSignal: number;

  // Current content
  currentContent: string;

  // Tool execution
  pendingToolExecutions: Map<string, ToolExecutionRequest>;

  // Error state
  error: string | null;

  // AbortController for cancellation
  abortController: AbortController | null;

  // Edit scope for scoped editing
  editScope: EditScope | null;

  // Auto-apply mode: when true, changes are applied without asking for approval
  autoApplyMode: boolean;

  // Agent mode: when true (default), assistant prioritizes creating agentic workflows
  agentMode: boolean;

  // Pending proposals for approval/rejection
  pendingProposals: Map<string, WorkflowProposal>;

  // Current multi-selection scope (captured when message is sent)
  // Used to validate that AI responses only modify selected nodes
  currentMultiSelectionScope: { type: 'multi'; nodeNames: string[] } | null;

  // Context window status (from Copilot SDK compaction events)
  contextUsagePercent: number | null; // 0.0 to 1.0
  contextStatus: 'normal' | 'warning' | 'compacting' | 'compacted' | null;
  copilotSessionId: string | null;

  // Model selection
  selectedModel: string; // User-selected LLM model name

  // Chat history persistence state
  currentChatId: string | null;
  isHistoryPanelOpen: boolean;
  chatHistoryVersion: number; // Incremented when chat is saved, to trigger list refresh

  // Unread message indicator (for rail collapse badge)
  hasUnreadMessages: boolean;

  // Pending prompt from AI entry points (picker, node editor, etc.)
  pendingPrompt: PendingPrompt | null;

  // Actions
  toggleSidebar: () => void;
  openSidebar: () => void;
  setEditScope: (scope: EditScope | null) => void;
  setAutoApplyMode: (enabled: boolean) => void;
  /** Record thumbs up/down feedback on the latest assistant turn and persist it. */
  setLatestAssistantFeedback: (sentiment: FeedbackSentiment) => void;
  setAgentMode: (enabled: boolean) => void;
  setSelectedModel: (model: string) => void;
  setSidebarWidth: (width: number) => void;
  sendMessage: (content: string, getToken: () => Promise<string | null>, azureContext: AzureContext, stickyScope?: StickyEditScope) => Promise<void>;
  cancelStream: () => void;
  clearChat: () => void;
  /** Clears chat messages but preserves the current session ID */
  clearChatMessages: () => void;
  handleToolExecutionRequest: (request: ToolExecutionRequest, getToken: () => Promise<string | null>, azureContext: AzureContext) => Promise<void>;
  clearError: () => void;
  setCurrentStatus: (status: string | null) => void;

  // Chat history actions
  saveCurrentChat: (workflowName: string, opts?: ChatHistoryFetchOptions) => Promise<void>;
  loadChat: (workflowName: string, chatId: string, opts?: ChatHistoryFetchOptions) => Promise<void>;
  setCurrentChatId: (chatId: string | null) => void;
  toggleHistoryPanel: () => void;
  openHistoryPanel: () => void;
  closeHistoryPanel: () => void;
  setHasUnreadMessages: (hasUnread: boolean) => void;
  queuePrompt: (content: string, scope: StickyEditScope, source: CopilotEntryPointSource, systemContext?: string) => void;
  clearPendingPrompt: () => void;

  /**
   * Answer a pending clarification. `optionId` identifies which option was
   * picked (omit for free-text / "Something else" answers). `text` is what
   * gets sent as the user's reply — for an option click, the caller passes
   * the option's sendValue ?? label; for custom text, the typed value.
   *
   * Side effects: marks the pending clarification snapshot as answered (so
   * it persists with the highlighted option), clears pendingClarification,
   * and dispatches the answer via sendMessage so it flows as a normal turn.
   */
  answerClarification: (text: string, optionId: string | undefined, getToken: () => Promise<string | null>, azureContext: AzureContext) => Promise<void>;

  // Proposal actions
  addProposal: (proposal: WorkflowProposal) => void;
  approveProposal: (id: string) => Promise<void>;
  rejectProposal: (id: string, reason?: string) => void;
  clearProposal: (id: string) => void;
  getProposal: (id: string) => WorkflowProposal | undefined;

  // Individual change actions
  approveChange: (proposalId: string, changeId: string) => void;
  rejectChange: (proposalId: string, changeId: string) => void;
  undoChangeDecision: (proposalId: string, changeId: string) => void;
  approveAllChanges: (proposalId: string) => void;
  rejectAllChanges: (proposalId: string) => void;
  applyApprovedChanges: (proposalId: string) => Promise<void>;

  // Connection assignment actions
  approveConnectionAssignment: (proposalId: string, requirementId: string, selectedReferenceKey: string) => void;
  rejectConnectionAssignment: (proposalId: string, requirementId: string) => void;
  approveAllConnectionAssignments: (proposalId: string) => void;
  skipAllConnectionAssignments: (proposalId: string) => void;
}

// Load sidebar width from localStorage
function loadSidebarWidth(): number {
  try {
    const stored = localStorage.getItem('aguiSidebarWidth');
    if (stored) {
      return parseInt(stored, 10);
    }
  } catch (e) {
    console.error('Failed to load sidebar width from localStorage', e);
  }
  return 400;
}

// Save sidebar width to localStorage
function saveSidebarWidth(width: number): void {
  try {
    localStorage.setItem('aguiSidebarWidth', width.toString());
  } catch (e) {
    console.error('Failed to save sidebar width to localStorage', e);
  }
}

// Predefined status messages for normalization
const PREDEFINED_STATUSES: Record<string, string> = {
  'starting': '🚀 Starting workflow generation...',
  'searching': '🔍 Searching for connectors...',
  'analyzing': '📊 Analyzing requirements...',
  'building': '🔨 Building workflow definition...',
  'validating': '✅ Validating workflow...',
  'applying': '📥 Applying to canvas...',
  'ready': '✨ Workflow ready!',
  'workflow ready': '✨ Workflow ready!',
};

// Normalize status text to predefined message if it matches a keyword
function normalizeStatus(statusText: string): string {
  const lowerText = statusText.toLowerCase();

  // Check for keyword matches
  for (const [keyword, fullMessage] of Object.entries(PREDEFINED_STATUSES)) {
    if (lowerText.includes(keyword)) {
      return fullMessage;
    }
  }

  // Return original if no match (e.g., for error messages)
  return statusText;
}

// Helper to get the latest STATUS: line from content (not "Workflow ready")
function getLatestStatus(content: string): string | null {
  const lines = content.split('\n');
  let latestStatus: string | null = null;

  for (const line of lines) {
    const match = line.match(/STATUS:\s*(.+)/);
    if (match && match[1]) {
      const statusText = match[1].trim();
      // Skip "Workflow ready" - handled separately after canvas application
      if (statusText.toLowerCase().includes('workflow ready') || statusText.includes('✨')) {
        continue;
      }
      // Only consider if it has meaningful text (more than just an emoji)
      if (statusText.length > 2) {
        latestStatus = normalizeStatus(statusText);
      }
    }
  }

  return latestStatus;
}

/**
 * Resolves the initial value for `selectedModel`. Honors a per-session
 * pick from `sessionStorage` first, then the user's Settings -> AI
 * assistant default. When neither is set the value is "Automatic"
 * (empty string), which defers to the backend's live catalog default
 * once `useModelCatalog` resolves.
 *
 * Wrapped in try/catch at each step because sessionStorage may be
 * unavailable (private-mode Safari) and the app store may not have
 * hydrated yet during very early boot.
 */
function resolveInitialModel(): string {
  try {
    const sessionPick = sessionStorage.getItem('otto:selectedModel');
    if (sessionPick) return sessionPick;
  } catch {
    // sessionStorage unavailable — fall through to the app-store default.
  }
  try {
    // Empty string = "Automatic" (ASSISTANT_MODEL_AUTOMATIC) — defers to the
    // backend's catalog default. Inlined rather than imported so module init
    // never depends on test mocks of appStore re-exporting the constant.
    return useAppStore.getState().assistantDefaultModel || '';
  } catch {
    return '';
  }
}

/**
 * Resolves the initial value for `agentMode` from the user's
 * Settings → Copilot chat default. The in-chat toggle (bot icon in the
 * composer) overrides this for the current session; the preference
 * only seeds the chat store when it first boots.
 *
 * Wrapped in try/catch because the app store may not have hydrated
 * during very early boot. Defaults to `true` (agent mode on) when the
 * lookup fails, matching the legacy behavior.
 */
function resolveInitialAgentMode(): boolean {
  try {
    return useAppStore.getState().assistantDefaultAgentMode !== false;
  } catch {
    return true;
  }
}

/**
 * Builds the current connections context to seed the assistant with, so it
 * reuses existing (draft) connections instead of recreating them.
 *
 * Connections are draft-aware: when the workflow is in draft mode we send
 * connections-draft.json, otherwise the saved connections.json. Both reads go
 * through the shared React Query cache (same keys as the connection hooks) so
 * we reuse already-fetched data when available. Failures are non-fatal — the
 * assistant falls back to its empty-connections behavior.
 *
 * App settings are intentionally NOT sent: their values can hold secrets
 * (connection keys, etc.) and the assistant's GetAppSettings tool can echo
 * them back to the model. Connection reuse only needs connections.json.
 */
async function buildAssistantConnectionsContext(
  getToken: () => Promise<string | null>,
): Promise<{ connectionsJson?: string | undefined }> {
  const { selectedProject, selectedApp, selectedAppName } = useAppStore.getState();
  const projectName = selectedApp?.projectName ?? selectedProject ?? '';
  const appName = selectedApp?.name ?? selectedAppName ?? '';
  if (!projectName || !appName) {
    return {};
  }

  const opts = { projectName, appName, getToken };
  const isDraftMode = useDraftModeStore.getState().isDraftMode;

  let connectionsJson: string | undefined;
  try {
    const config = isDraftMode
      ? await queryClient.fetchQuery({
          queryKey: ['connections-draft', projectName.toLowerCase(), appName.toLowerCase()],
          queryFn: () => fetchConnectionsDraftJson(opts),
          staleTime: 5 * 60 * 1000,
        })
      : await queryClient.fetchQuery({
          queryKey: ['connections', projectName.toLowerCase(), appName.toLowerCase()],
          queryFn: () => fetchConnectionsJson(opts),
          staleTime: 5 * 60 * 1000,
        });
    if (config && Object.keys(config).length > 0) {
      // @appsetting('key') / @appsetting("key") values are secrets — replace them
      // with a dummy before sending; the assistant shouldn't need real app-settings
      // values. Do this in the JSON.stringify replacer so it runs on the raw string
      // values (handling both quote styles) instead of the JSON-escaped output.
      connectionsJson = JSON.stringify(config, (_key, value) =>
        typeof value === 'string'
          ? value.replace(
              /@\{?\s*appsettings?\(\s*['"]([^'"]*)['"]\s*\)\s*\}?/g,
              (_match, name) => `stubbed-appsetting-${name}`,
            )
          : value,
      );
    }
  } catch (e) {
    console.warn('[AG-UI] Failed to load connections for assistant context:', e);
  }

  return { connectionsJson };
}

export const useAgentChatAguiStore = create<AgentChatAguiState>()((set, get) => ({
  // Initial state
  isOpen: true,
  sidebarWidth: loadSidebarWidth(),
  chatMessages: [],
  currentStatus: null,
  currentTurnStages: {},
  pendingClarification: null,
  messageHistory: [],
  isStreaming: false,
  feedbackEligible: false,
  feedbackSaveSignal: 0,
  currentContent: '',
  pendingToolExecutions: new Map(),
  error: null,
  abortController: null,
  editScope: null,
  autoApplyMode: true, // Default: Apply changes after proposal
  agentMode: resolveInitialAgentMode(),
  pendingProposals: new Map(),
  currentMultiSelectionScope: null, // No multi-selection scope initially
  contextUsagePercent: null,
  contextStatus: null,
  copilotSessionId: null,
  selectedModel: resolveInitialModel(),
  currentChatId: null, // No active chat session by default
  isHistoryPanelOpen: false, // Chat history panel closed by default
  chatHistoryVersion: 0, // Version counter for chat history refresh
  hasUnreadMessages: false, // No unread messages initially
  pendingPrompt: null, // No queued AI prompt initially
  pendingSystemContext: null, // No hidden context initially

  toggleSidebar: () => {
    set((state) => ({ isOpen: !state.isOpen }));
  },

  openSidebar: () => {
    set({ isOpen: true });
  },

  setEditScope: (scope: EditScope | null) => {
    set({ editScope: scope });
  },

  setAutoApplyMode: (enabled: boolean) => {
    set({ autoApplyMode: enabled });
  },

  setLatestAssistantFeedback: (sentiment: FeedbackSentiment) => {
    set((state) => {
      const idx = latestFeedbackAnchorIndex(state.chatMessages);
      if (idx < 0) return {};
      const messages = [...state.chatMessages];
      messages[idx] = { ...messages[idx]!, feedback: sentiment };
      return {
        chatMessages: messages,
        feedbackSaveSignal: state.feedbackSaveSignal + 1,
        feedbackEligible: false,
      };
    });
  },

  setAgentMode: (enabled: boolean) => {
    set({ agentMode: enabled });
  },

  setSelectedModel: (model: string) => {
    try { sessionStorage.setItem('otto:selectedModel', model); } catch { /* noop */ }
    set({ selectedModel: model });
  },

  setSidebarWidth: (width: number) => {
    saveSidebarWidth(width);
    set({ sidebarWidth: width });
  },

  clearError: () => {
    set({ error: null });
  },

  setCurrentStatus: (status: string | null) => {
    set({ currentStatus: status });
  },

  sendMessage: async (content: string, getToken: () => Promise<string | null>, azureContext: AzureContext, stickyScope?: StickyEditScope) => {
    const state = get();
    if (state.isStreaming) return;

    const timestamp = Date.now();

    // Use the sticky scope passed from the UI
    // If no sticky scope provided, default to full workflow mode
    const effectiveScope = stickyScope || { type: 'workflow' as const };
    
    const workflowStore = useWorkflowStore.getState();
    
    let scopeWithDefinition: import('@/services/aguiStreamClient').EditScope | undefined;
    // Track if we're in a scoped edit mode (single node or multi-node)
    const isScoped = effectiveScope.type !== 'workflow';

    // Pin this turn to the workflow that is open right now — captured
    // synchronously BEFORE any `await` below (the first is getToken()). If the
    // user opens a different workflow before the stream finishes, the
    // auto-apply/proposal handlers use this to avoid overwriting the
    // newly-opened workflow. Capturing this AFTER an await would race: the
    // active workflow could already have changed during token acquisition,
    // reopening issue #2154. `currentWorkflowIdentity` is replaced (not
    // mutated) on switch, so this snapshot stays pinned to the original.
    const requestWorkflowIdentity = workflowStore.currentWorkflowIdentity;

    // Coarse fallback key for the #2154 guard, also captured synchronously
    // before any `await`. When the designer's identity effect hasn't hydrated
    // yet (needs the MSAL account + tenant), `requestWorkflowIdentity` is null
    // and the guard would be disabled. `azureContext.workflowName` and the app
    // store are populated immediately, so this coarse key still lets the
    // auto-apply/proposal handlers detect a mid-stream workflow switch during
    // that hydration window.
    const appStateForFallback = useAppStore.getState();
    const requestWorkflowFallbackKey = azureContext.workflowName
      ? composeWorkflowFallbackKey(
          appStateForFallback.selectedApp?.projectName ?? appStateForFallback.selectedProject ?? '',
          appStateForFallback.selectedApp?.name ?? appStateForFallback.selectedAppName ?? '',
          azureContext.workflowName,
        )
      : null;

    if (effectiveScope.type === 'multi' && effectiveScope.nodeNames && effectiveScope.nodeNames.length > 1) {
      // Multi-selection mode - build scope with all nodes from sticky scope
      const nodeNames = effectiveScope.nodeNames;
      const workflow = workflowStore.workflow;
      const nodes: ScopedNode[] = [];
      for (const nodeName of nodeNames) {
        const path = workflow ? resolveNodePath(workflow, nodeName) : undefined;
        if (!path) {
          console.warn(`[AG-UI] Could not resolve jsonPath for node "${nodeName}" — sending without path`);
          nodes.push({ name: nodeName });
        } else {
          nodes.push({ name: nodeName, jsonPath: path });
        }
      }

      scopeWithDefinition = {
        type: 'node',
        nodes
      };
      
      // Store the multi-selection scope for validation of AI responses
      set({ currentMultiSelectionScope: { type: 'multi', nodeNames } });
    } else if (effectiveScope.type === 'single' && effectiveScope.nodeNames && effectiveScope.nodeNames.length === 1) {
      // Single node scope from sticky scope
      set({ currentMultiSelectionScope: null });
      const nodeName = effectiveScope.nodeNames[0]!;
      // Resolve jsonPath for the node (type-agnostic)
      const jsonPath = effectiveScope.jsonPath || (workflowStore.workflow ? resolveNodePath(workflowStore.workflow, nodeName) : undefined);
      
      if (!jsonPath) {
        console.warn(`[AG-UI] Could not resolve jsonPath for node "${nodeName}" — sending without path`);
      }
      scopeWithDefinition = {
        type: 'node',
        nodes: [jsonPath ? { name: nodeName, jsonPath } : { name: nodeName }]
      };
    } else {
      // Workflow mode - no scope
      set({ currentMultiSelectionScope: null });
    }

    // Build editScope from scopeWithDefinition for use when applying proposals
    // This ensures proposal application uses the correct scope from THIS message
    let editScopeForMessage: EditScope | null = null;
    if (scopeWithDefinition) {
      if (scopeWithDefinition.nodes && scopeWithDefinition.nodes.length === 1) {
        const node = scopeWithDefinition.nodes[0]!;
        editScopeForMessage = {
          type: 'node',
          name: node.name,
          ...(node.jsonPath ? { jsonPath: node.jsonPath } : {}),
        };
      } else if (scopeWithDefinition.nodes && scopeWithDefinition.nodes.length > 1) {
        // Multi-selection maps to workflow type for proposals (applies as full workflow)
        // but we preserve node names so ProposalCard can display them
        const nodeNames = scopeWithDefinition.nodes.map(n => n.name);
        editScopeForMessage = { type: 'workflow', nodeNames };
      }
    }

    // Add user message to internal history
    const userMessage: AguiMessage = {
      id: `user-${timestamp}`,
      role: 'user',
      content,
      timestamp
    };

    // Add user message to visible chat
    const userChatMessage: ChatMessage = {
      id: `chat-user-${timestamp}`,
      role: 'user',
      content,
      timestamp
    };

    // Track message sent telemetry
    const scopeLabel = effectiveScope.type === 'single' ? 'action' : 'workflow';
    telemetryService.trackEvent('agent.chat.message_sent', { scope: scopeLabel });

    // Add user message and start streaming.
    // Track the position of the user message we just added so we can later
    // scope stage attribution to "the assistant messages added AFTER this
    // point" — without this, run_finished could attach the new turn's stages
    // to an assistant message from a previous turn. Also reset
    // currentTurnStages so stale state from a prior cancelled/errored turn
    // can't leak into this one.
    const turnStartIndex = state.chatMessages.length + 1; // +1 because we're about to push userChatMessage
    set({
      messageHistory: [...state.messageHistory, userMessage],
      chatMessages: [...state.chatMessages, userChatMessage],
      currentStatus: '💭 Thinking...',
      currentTurnStages: {},
      isStreaming: true,
      feedbackEligible: false,
      error: null,
      currentContent: '',
      editScope: editScopeForMessage,
    });

    // Create abort controller
    const abortController = new AbortController();
    set({ abortController });

    // Build conversation history for context
    // Filter out any messages with empty content to prevent API errors
    const conversationHistory: MessageHistory[] = state.messageHistory
      .filter(m => m.role === 'user' || m.role === 'assistant')
      .filter(m => !m.isToolExecution)
      .filter(m => m.content && m.content.trim().length > 0)  // Ensure non-empty content
      .map(m => ({ role: m.role, content: m.content }));

    try {
      const { agentMode } = get();
      // Only send agentMode when editing full workflow, not when scoped to action/trigger
      const effectiveAgentMode = isScoped ? false : agentMode;

      // Acquire Otto dataplane token for authorization
      const ottoAccessToken = await getToken();
      
      // Get current workflow context for editing existing workflows
      const currentWorkflow = workflowStore.workflow;
      const workflowContext = currentWorkflow ? JSON.stringify(currentWorkflow, null, 2) : undefined;
      const nodeCount = effectiveScope.nodeNames?.length ?? 0;

      // Load the current (draft-aware) connections so the assistant reuses
      // existing connections instead of recreating them.
      const { connectionsJson } = await buildAssistantConnectionsContext(getToken);
      
      // Debug logging
      console.log('[AG-UI] sendMessage context:', {
        hasWorkflow: !!currentWorkflow,
        workflowContextLength: workflowContext?.length,
        hasConnections: !!connectionsJson,
        connectionsLength: connectionsJson?.length,
        scopeType: effectiveScope.type,
        nodeCount,
        isScoped
      });
      
      // Track whether a workflow_proposal event was already processed
      // to prevent the text_message_end legacy handler from creating duplicates
      let proposalAlreadyHandled = false;

      // Track no_changes message IDs so we can remove them if a workflow_proposal arrives later.
      // In agent mode the backend may emit intermediate no_changes events before eventually
      // producing a successful proposal — those stale "no changes" messages should be cleaned up.
      const noChangesMessageIds: string[] = [];

      // Forward only the user's Settings → Copilot chat preferences that
      // the assistant backend actually consumes. The reasoningEffort /
      // globalInstructions fields existed in an earlier draft but the
      // ChatRequest DTO doesn't accept them, so we don't send them.

      await streamAguiMessage({
        prompt: content,
        projectName: useAppStore.getState().selectedProject ?? '',
        accessToken: ottoAccessToken!,
        messages: conversationHistory,
        agentMode: effectiveAgentMode,
        model: get().selectedModel,
        // Pass existing sessionId for session reuse (avoids new CopilotSession per request)
        ...(get().copilotSessionId ? { sessionId: get().copilotSessionId as string } : {}),
        ...(scopeWithDefinition ? { scope: scopeWithDefinition } : {}),
        ...(workflowContext ? { workflowContext } : {}),
        ...(connectionsJson ? { connectionsJson } : {}),
        signal: abortController.signal,
        onEvent: (event: AguiEvent) => {
          const currentState = get();

          if (event.type === 'text_message_start') {
            // Reset content accumulator for new message
            set({ currentContent: '' });
          } else if (event.type === 'text_message_content') {
            // Accumulate content internally
            const newContent = currentState.currentContent + (event.content || '');
            set({ currentContent: newContent });

            // Get the latest status and update (replaces, doesn't accumulate)
            const latestStatus = getLatestStatus(newContent);
            if (latestStatus && latestStatus !== currentState.currentStatus) {
              set({ currentStatus: latestStatus });
            }
          } else if (event.type === 'text_message_end') {
            // Finalize message - add to internal history
            // Use accumulated content, or a fallback if somehow empty
            const finalContent = currentState.currentContent || '[Assistant processed the request]';

            // Strip JSON code blocks and STATUS lines from the persisted message
            // (the streaming view already does this; saved messages should match)
            const cleanedContent = finalContent
              .replace(/```(?:json)?[\s\S]*?```/g, '')
              .replace(/^STATUS:.*$/gm, '')
              .replace(/\n{3,}/g, '\n\n')
              .trim() || '[Assistant processed the request]';

            const assistantMessage: AguiMessage = {
              id: `assistant-${Date.now()}`,
              role: 'assistant',
              content: cleanedContent,
              timestamp: Date.now()
            };

            set((state) => ({
              messageHistory: [...state.messageHistory, assistantMessage],
              currentContent: ''
            }));

            // Handle full workflow generation (legacy text extraction path)
            // Skip if workflow_proposal event already handled the JSON
            if (proposalAlreadyHandled) {
              // Structured workflow_proposal event was already processed — skip legacy path
              set({ currentStatus: null });
            } else {
              // Full workflow generation
              const workflowJson = extractWorkflowJson(finalContent);
              if (workflowJson) {
                // Guard against overwriting a different workflow than the one
                // this turn was started for (issue #2154). If the user switched
                // workflows mid-stream, withhold the changes and tell them why.
                if (!isActiveWorkflow(requestWorkflowIdentity, requestWorkflowFallbackKey)) {
                  console.warn('[AG-UI] Workflow changed during streaming (legacy path) — withholding changes to avoid overwrite');
                  set((state) => ({
                    chatMessages: [...state.chatMessages, buildWorkflowSwitchedMessage(requestWorkflowIdentity)],
                    currentStatus: null,
                  }));
                  return;
                }

                const { autoApplyMode } = get();

                // Get baseline for comparison
                const currentWorkflow = useWorkflowStore.getState().workflow;
                const baselineJson = currentWorkflow ? JSON.stringify(currentWorkflow, null, 2) : '{}';

                // Check if workflow is empty (no actions and no triggers)
                const isEmptyWorkflow = !currentWorkflow ||
                  (Object.keys(currentWorkflow.definition?.actions || {}).length === 0 &&
                   Object.keys(currentWorkflow.definition?.triggers || {}).length === 0);

                // Validate and compute diff
                const validation = validateProposedJson(workflowJson, 'workflow');

                // If JSON completely failed to parse (syntax error), skip proposal
                if (validation.parseError) {
                  console.warn('[AG-UI] Legacy path: Invalid workflow JSON, skipping:', validation.error);
                  set({ currentStatus: null });
                  return;
                }

                // If JSON is structurally invalid (e.g., missing 'definition' property),
                // skip the proposal card but show the original message (likely sample data)
                if (!validation.valid) {
                  console.warn('[AG-UI] Legacy path: Structurally invalid workflow JSON, showing as message:', validation.error);
                  const infoMessage: ChatMessage = {
                    id: `chat-info-${Date.now()}`,
                    role: 'assistant',
                    content: finalContent.trim(),
                    timestamp: Date.now()
                  };
                  set((state) => ({
                    chatMessages: [...state.chatMessages, infoMessage],
                    currentStatus: null
                  }));
                  return;
                }

                // Format proposed JSON for line number calculation
                const proposedJsonFormatted = validation.parsed ? JSON.stringify(validation.parsed, null, 2) : workflowJson;
                const diff = computeJsonDiff(
                  currentWorkflow || {},
                  validation.parsed || {},
                  baselineJson,
                  proposedJsonFormatted
                );

                // Check multi-selection scope validation before auto-apply
                // Note: validation.valid is always true here (invalid JSON filtered earlier)
                const { currentMultiSelectionScope } = get();
                let scopeValidationResult2: ReturnType<typeof validateMultiSelectionScope> | undefined;
                if (currentMultiSelectionScope && validation.parsed) {
                  scopeValidationResult2 = validateMultiSelectionScope(
                    validation.parsed as import('@/types/workflowDefinition').WorkflowDefinition,
                    currentWorkflow || null,
                    currentMultiSelectionScope.nodeNames
                  );
                }
                const scopeIsValid2 = !scopeValidationResult2 || scopeValidationResult2.isValid;

                // Auto-apply if: (1) workflow is empty (first creation), OR (2) autoApplyMode is enabled AND scope is valid
                if ((isEmptyWorkflow || autoApplyMode) && scopeIsValid2) {
                  set({ currentStatus: '📥 Applying to canvas...' });

                  // Analyze connection requirements before applying
                  let connectionMatching: ConnectionMatchingResult | undefined;
                  if (validation.parsed) {
                    const { references } = useConnectionStore.getState();
                    const proposedWorkflow = validation.parsed as import('@/types/workflowDefinition').WorkflowDefinition;
                    connectionMatching = analyzeConnectionRequirements(
                      proposedWorkflow,
                      currentWorkflow || null,
                      references
                    );
                  }

                  // Apply connection assignments to workflow before applying
                  let workflowToApply = workflowJson;
                  if (connectionMatching && validation.parsed) {
                    try {
                      // Auto-approve proposals that have a compatible connection match
                      for (const proposal of connectionMatching.needsApproval) {
                        if (proposal.status === 'pending' && proposal.selectedConnection) {
                          proposal.status = 'approved';
                        }
                      }
                      const updated = applyConnectionAssignments(
                        validation.parsed as import('@/types/workflowDefinition').WorkflowDefinition,
                        connectionMatching
                      );
                      workflowToApply = JSON.stringify(updated, null, 2);
                    } catch (e) {
                      console.warn('[AG-UI] Failed to apply connection assignments:', e);
                    }
                  }

                  // Apply directly without creating a pending proposal
                  const success = applyWorkflowToCanvas(workflowToApply);
                  if (success) {
                    appendConnectionSetupMessage(set, connectionMatching);
                  }
                } else {
                  // Ask mode: create proposal for user review
                  set({ currentStatus: '📋 Review proposed changes...' });

                  // Build scope validation error message if applicable (reuse scopeValidationResult2 from above)
                  let scopeValidationError2: string | undefined;
                  if (scopeValidationResult2 && !scopeValidationResult2.isValid) {
                    const outOfScopeNames = scopeValidationResult2.outOfScopeChanges
                      .map(c => `${c.name} (${c.changeType})`)
                      .join(', ');
                    scopeValidationError2 = `Out-of-scope modifications detected: ${outOfScopeNames}. Only the selected nodes (${currentMultiSelectionScope!.nodeNames.join(', ')}) can be modified.`;
                    console.warn('[AG-UI] Multi-selection scope violation (text extraction):', scopeValidationResult2.outOfScopeChanges);
                  }

                  // Analyze connection requirements for workflow proposals
                  let connectionMatching: ConnectionMatchingResult | undefined;
                  if (validation.parsed) {
                    const { references } = useConnectionStore.getState();
                    const proposedWorkflow = validation.parsed as import('@/types/workflowDefinition').WorkflowDefinition;
                    connectionMatching = analyzeConnectionRequirements(
                      proposedWorkflow,
                      currentWorkflow || null,
                      references
                    );
                  }

                  // Create proposal (validation.valid is always true here - invalid JSON filtered earlier)
                  const proposal: WorkflowProposal = {
                    id: crypto.randomUUID(),
                    type: 'workflow',
                    proposedJson: workflowJson,
                    baselineJson,
                    diff,
                    status: scopeValidationError2 ? 'invalid' : !diff.hasChanges ? 'no-change' : 'pending',
                    timestamp: Date.now(),
                    messageId: `assistant-${Date.now()}`,
                    ...(requestWorkflowIdentity ? { workflowIdentity: requestWorkflowIdentity } : {}),
                    ...(requestWorkflowFallbackKey ? { workflowFallbackKey: requestWorkflowFallbackKey } : {}),
                    ...(scopeValidationError2 && { validationError: scopeValidationError2 }),
                    // Add connection matching results
                    ...(connectionMatching && {
                      connectionMatching,
                      connectionsResolved: connectionMatching.needsApproval.length === 0,
                    }),
                  };

                  get().addProposal(proposal);
                }
              } else {
                // No JSON found - backend should have sent structured events
                // Just clear status; any messages should come via assistant_message or no_changes events
                set({ currentStatus: null });
              }
            }
          } else if (event.type === 'tool_execution_request') {
            // DEDUPLICATION: Check if this execution_id was already handled
            const executionId = event.execution_id;
            if (!executionId) {
              console.warn('[AG-UI] tool_execution_request missing execution_id, skipping');
              return;
            }

            const { pendingToolExecutions } = get();
            if (pendingToolExecutions.has(executionId)) {
              console.log(`[AG-UI] Duplicate tool_execution_request for ${executionId}, skipping`);
              return;
            }

            // Update status based on which tool is being called, including relevant arguments
            const args = event.arguments || {};
            let toolStatus: string;

            switch (event.tool_name) {
              case 'SearchOperations': {
                const searchText = args.searchText as string;
                const connFilter = args.connectorNameFilter as string;
                toolStatus = connFilter
                  ? `🔍 Searching "${searchText}" operations in ${connFilter}...`
                  : `🔍 Searching "${searchText}" operations...`;
                break;
              }
              case 'GetActionTemplate': {
                const connector = args.connectorName as string;
                const operation = args.operationId as string;
                toolStatus = connector && operation
                  ? `🧩 Getting template for ${connector}/${operation}...`
                  : '🧩 Getting action template...';
                break;
              }
              case 'ValidateWorkflow':
                toolStatus = '✅ Validating workflow...';
                break;
              case 'GetWorkflowSchema':
                toolStatus = '📊 Analyzing workflow structure...';
                break;
              case 'ListKnowledgeHubs':
                toolStatus = '📚 Loading knowledge hubs...';
                break;
              default:
                toolStatus = `🔧 Executing ${event.tool_name}...`;
            }

            const request: ToolExecutionRequest = {
              execution_id: executionId,
              tool_name: event.tool_name!,
              arguments: event.arguments || {}
            };

            // Mark as pending BEFORE async execution to prevent duplicate calls
            set((state) => ({
              currentStatus: toolStatus,
              pendingToolExecutions: new Map(state.pendingToolExecutions).set(executionId, request),
            }));

            get().handleToolExecutionRequest(request, getToken, azureContext);
          } else if (event.type === 'run_error') {
            // Structured failure from the backend orchestrator. The error
            // message itself is always present; `event.reason` /
            // `event.last_errors` / `event.last_known_good_workflow_json`
            // are populated when the failure has structured context that
            // lets us render an actionable card. The fallback path (no
            // structured payload) shows the same simple bubble as before.
            const userFriendlyError = sanitizeErrorMessage(event.error);
            telemetryService.trackEvent('agent.chat.error', {
              error: userFriendlyError,
              reason: event.reason ?? 'unspecified',
            });
            const errorMessage: ChatMessage = {
              id: `chat-error-${Date.now()}`,
              role: 'assistant',
              content: formatStructuredErrorMessage(userFriendlyError, event),
              timestamp: Date.now()
            };
            const assistantErrorHistoryMessage: AguiMessage = {
              id: `assistant-error-${Date.now()}`,
              role: 'assistant',
              content: errorMessage.content,
              timestamp: Date.now(),
            };
            set((state) => ({
              error: userFriendlyError,
              currentStatus: null,
              chatMessages: [...state.chatMessages, errorMessage],
              messageHistory: [...state.messageHistory, assistantErrorHistoryMessage],
            }));
          }
          // NEW: Handle structured events from backend filtering
          else if (event.type === 'clarification_request') {
            // Backend is asking the user a question — any of Plan /
            // Build can emit this. Build the snapshot, attach it to the
            // most-recent assistant message (or a synthesized placeholder),
            // and set pendingClarification so the chat input is disabled
            // until the user answers.
            const snapshot: ClarificationSnapshot = {
              messageId: event.message_id ?? `clarify-${Date.now()}`,
              ...(event.stage ? { stage: event.stage as PipelineStageId } : {}),
              question: event.question ?? '',
              options: event.options ?? [],
              allowCustom: event.allow_custom ?? true,
            };

            // Clarification-only runs may not emit text_message_end or
            // assistant_message. Persist a compact assistant turn so the next
            // AGUI request includes this interaction in `messages`.
            const optionsText = snapshot.options.map((o) => o.label).join(' | ');
            const clarificationHistoryText = optionsText.length > 0
              ? `Clarification requested: ${snapshot.question}\nOptions: ${optionsText}`
              : `Clarification requested: ${snapshot.question}`;
            const clarificationHistoryMessage: AguiMessage = {
              id: `assistant-clarification-${Date.now()}`,
              role: 'assistant',
              content: clarificationHistoryText,
              timestamp: Date.now(),
            };

            // Snapshot stages collected so far onto the same message that
            // owns the clarification, so the persisted record carries both.
            set((state) => {
              const messages = [...state.chatMessages];
              // Place the snapshot on the most-recent assistant message in
              // THIS turn (>= turnStartIndex), or synthesize one.
              let attached = false;
              for (let i = turnStartIndex; i < messages.length; i++) {
                if (messages[i]?.role === 'assistant') {
                  messages[i] = { ...messages[i]!, clarification: snapshot };
                  attached = true;
                  break;
                }
              }
              if (!attached) {
                messages.push({
                  id: snapshot.messageId,
                  role: 'assistant',
                  content: '',
                  timestamp: Date.now(),
                  clarification: snapshot,
                });
              }
              return {
                chatMessages: messages,
                pendingClarification: snapshot,
                messageHistory: [...state.messageHistory, clarificationHistoryMessage],
              };
            });
          }
          else if (event.type === 'status_update') {
            // Real-time status update from backend.
            //
            // New shape: when `event.stage` is present the event belongs to a
            // pipeline stage and is rendered as a collapsible card in
            // {@link TurnStagesPanel}. The backend ships the current snapshot of
            // the stage on each emit, so we replace our per-stage entry; in
            // addition we accumulate the incremental `progress`/`started` emits
            // into a client-side `events` list (deduping consecutive
            // duplicates) so the card can show the per-tool history.
            //
            // Legacy shape: when `event.stage` is absent (older backends, or
            // ad-hoc compaction/notification status), we fall back to the
            // single-line `currentStatus` indicator.
            const statusText = event.emoji
              ? `${event.emoji} ${event.message || ''}`
              : (event.message || '');

            if (event.stage) {
              set((state) => {
                const stageId = event.stage as PipelineStageId;
                const prev = state.currentTurnStages[stageId];
                const step = event.step ?? 'progress';
                const line = (event.message ?? '').trim();

                const prevEvents = prev?.events ?? [];
                const isIncremental = step === 'progress' || step === 'started';
                const last = prevEvents[prevEvents.length - 1];
                const isDuplicate = !!last && last.summary === line && last.emoji === event.emoji;
                const nextEvents = isIncremental && line && !isDuplicate
                  ? [
                      ...prevEvents,
                      {
                        summary: line,
                        step,
                        timestamp: Date.now(),
                        ...(event.emoji ? { emoji: event.emoji } : {}),
                      } as StageProgressEvent,
                    ]
                  : prevEvents;

                const snapshot: StageEventSnapshot = {
                  stage: stageId,
                  step,
                  summary: event.message ?? '',
                  timestamp: Date.now(),
                  ...(event.emoji ? { emoji: event.emoji } : {}),
                  ...(event.details ? { details: event.details } : {}),
                  ...(nextEvents.length ? { events: nextEvents } : {}),
                };
                return {
                  currentTurnStages: {
                    ...state.currentTurnStages,
                    [stageId]: snapshot,
                  },
                };
              });
            } else if (statusText) {
              set({ currentStatus: statusText });
            }

            // Whether or not the event carried stage info, accumulate the
            // human-readable line into the running context so the planner's
            // next turn sees what the prior turn announced.
            if (statusText) {
              const statusLine = `STATUS: ${statusText}`;
              set((state) => ({
                currentContent: state.currentContent
                  ? `${state.currentContent}\n${statusLine}`
                  : statusLine
              }));
            }
          } else if (event.type === 'context_status') {
            // Context window usage / compaction notification from Copilot SDK
            const usagePercent = event.usage_percent ?? null;
            const contextStatus = event.status ?? null;
            const contextMessage = event.message ?? '';

            set({ contextUsagePercent: usagePercent, contextStatus: contextStatus });

            // Show compaction status as a transient status indicator
            if (contextStatus === 'compacting') {
              set({ currentStatus: `🔄 ${contextMessage || 'Compacting conversation history...'}` });
            } else if (contextStatus === 'compacted') {
              set({ currentStatus: `✅ ${contextMessage || 'Context compacted'}` });
              // Clear the compaction status after 3 seconds
              setTimeout(() => {
                set((state) => ({
                  currentStatus: state.currentStatus?.startsWith('✅') ? null : state.currentStatus
                }));
              }, 3000);
            } else if (contextStatus === 'warning' && usagePercent !== null) {
              // Show a brief warning when context usage crosses the warning threshold
              set({ currentStatus: `⚠️ Context window is ${Math.round(usagePercent * 100)}% full` });
              setTimeout(() => {
                set((state) => ({
                  currentStatus: state.currentStatus?.startsWith('⚠️ Context') ? null : state.currentStatus
                }));
              }, 5000);
            }
          } else if (event.type === 'workflow_proposal') {
            // Workflow/action/trigger JSON proposal from backend
            // Mark as handled so text_message_end doesn't create a duplicate
            proposalAlreadyHandled = true;

            // Structured proposal flows often skip text_message_end and
            // assistant_message, so persist a compact assistant turn here to
            // keep subsequent AGUI requests context-complete.
            const proposalTypeLabel = event.proposal_type === 'workflow' ? 'workflow' : 'node';
            const proposalHistoryMessage: AguiMessage = {
              id: `assistant-proposal-${Date.now()}`,
              role: 'assistant',
              content: `[Assistant proposed ${proposalTypeLabel} changes]`,
              timestamp: Date.now(),
            };
            set((state) => ({
              messageHistory: [...state.messageHistory, proposalHistoryMessage],
            }));

            // Remove any stale "no changes" messages that were added during earlier
            // agent iterations — a proposal arriving means changes WERE generated.
            if (noChangesMessageIds.length > 0) {
              set((state) => ({
                chatMessages: state.chatMessages.filter(
                  (msg) => !noChangesMessageIds.includes(msg.id)
                ),
              }));
              noChangesMessageIds.length = 0;
            }

            let jsonContent = event.json_content || '';

            if (jsonContent) {
              // Guard against overwriting a different workflow than the one this
              // turn was started for (issue #2154). If the user switched
              // workflows mid-stream, withhold the changes and tell them why.
              if (!isActiveWorkflow(requestWorkflowIdentity, requestWorkflowFallbackKey)) {
                console.warn('[AG-UI] Workflow changed during streaming (workflow_proposal) — withholding changes to avoid overwrite');
                set((state) => ({
                  chatMessages: [...state.chatMessages, buildWorkflowSwitchedMessage(requestWorkflowIdentity)],
                  currentStatus: null,
                }));
                return;
              }

              const { autoApplyMode } = get();
              const { editScope } = get();
              const { currentMultiSelectionScope } = get();

              // Validate the JSON
              const validation = validateProposedJson(jsonContent, 'workflow');

              // If JSON completely failed to parse (syntax error), don't show a proposal card
              // Just log the error and continue - the AI message should explain what happened
              if (validation.parseError) {
                console.warn('[AG-UI] Received invalid JSON from backend, skipping proposal:', validation.error);
                set({ currentStatus: null });
                // Don't create a proposal for unparseable JSON - it just confuses users
                return;
              }

              // If JSON is structurally invalid (e.g., missing required 'type' property for action/trigger),
              // skip the proposal card but show the JSON as a message (likely sample data for the user)
              if (!validation.valid) {
                console.warn('[AG-UI] Received structurally invalid JSON, showing as message:', validation.error);
                // Format the JSON nicely and wrap in code block for display
                let formattedJson = jsonContent;
                try {
                  formattedJson = JSON.stringify(JSON.parse(jsonContent), null, 2);
                } catch {
                  // Keep original if parsing fails
                }
                const infoMessage: ChatMessage = {
                  id: `chat-sample-${Date.now()}`,
                  role: 'assistant',
                  content: `Here's the sample data:\n\n\`\`\`json\n${formattedJson}\n\`\`\``,
                  timestamp: Date.now()
                };
                set((state) => ({
                  chatMessages: [...state.chatMessages, infoMessage],
                  currentStatus: null
                }));
                return;
              }

              // Get baseline for comparison
              const workflowStore = useWorkflowStore.getState();
              const currentWorkflow = workflowStore.workflow;
              const baselineJson = currentWorkflow ? JSON.stringify(currentWorkflow, null, 2) : '{}';

              // Compute diff
              const proposedJsonFormatted = validation.parsed ? JSON.stringify(validation.parsed, null, 2) : jsonContent;
              const diff = computeJsonDiff(
                JSON.parse(baselineJson || '{}'),
                validation.parsed || {},
                baselineJson,
                proposedJsonFormatted
              );

              // Check if workflow is empty (for auto-apply on first creation)
              const isEmptyWorkflow = !currentWorkflow ||
                (Object.keys(currentWorkflow.definition?.actions || {}).length === 0 &&
                 Object.keys(currentWorkflow.definition?.triggers || {}).length === 0);

              // Check multi-selection scope validation before auto-apply
              let scopeValidationResult: ReturnType<typeof validateMultiSelectionScope> | undefined;
              if (currentMultiSelectionScope && validation.valid && validation.parsed) {
                scopeValidationResult = validateMultiSelectionScope(
                  validation.parsed as import('@/types/workflowDefinition').WorkflowDefinition,
                  currentWorkflow || null,
                  currentMultiSelectionScope.nodeNames
                );
              }

              // Auto-apply if: (empty workflow or autoApplyMode enabled) AND valid AND scope is valid (if multi-select)
              const scopeIsValid = !scopeValidationResult || scopeValidationResult.isValid;
              const shouldAutoApply = (isEmptyWorkflow || autoApplyMode) && validation.valid && scopeIsValid;
              let autoApplySuccess = false;

              if (shouldAutoApply) {
                set({ currentStatus: '📥 Applying to canvas...' });

                // Analyze and apply connection assignments before applying to canvas
                let connectionMatching: ConnectionMatchingResult | undefined;
                if (validation.parsed) {
                  const { references } = useConnectionStore.getState();
                  const proposedWorkflow = validation.parsed as import('@/types/workflowDefinition').WorkflowDefinition;
                  connectionMatching = analyzeConnectionRequirements(
                    proposedWorkflow,
                    currentWorkflow || null,
                    references
                  );

                  if (connectionMatching) {
                    for (const proposal of connectionMatching.needsApproval) {
                      if (proposal.status === 'pending' && proposal.selectedConnection) {
                        proposal.status = 'approved';
                      }
                    }
                    try {
                      const updated = applyConnectionAssignments(
                        proposedWorkflow,
                        connectionMatching
                      );
                      jsonContent = JSON.stringify(updated, null, 2);
                    } catch (e) {
                      console.warn('[AG-UI] Failed to apply connection assignments:', e);
                    }
                  }
                }

                autoApplySuccess = applyWorkflowToCanvas(jsonContent);

                if (autoApplySuccess) {
                  appendConnectionSetupMessage(set, connectionMatching);
                } else {
                  console.warn('[AG-UI] Auto-apply failed, falling through to proposal creation');
                }
              }

              // Create proposal for user review when auto-apply was not attempted or failed
              if (!autoApplySuccess) {
                // Create proposal for user review
                set({ currentStatus: '📋 Review proposed changes...' });

                // Build scope validation error message if applicable (reuse scopeValidationResult from above)
                let scopeValidationError: string | undefined;
                if (scopeValidationResult && !scopeValidationResult.isValid) {
                  const outOfScopeNames = scopeValidationResult.outOfScopeChanges
                    .map(c => `${c.name} (${c.changeType})`)
                    .join(', ');
                  scopeValidationError = `Out-of-scope modifications detected: ${outOfScopeNames}. Only the selected nodes (${currentMultiSelectionScope!.nodeNames.join(', ')}) can be modified.`;
                  console.warn('[AG-UI] Multi-selection scope violation:', scopeValidationResult.outOfScopeChanges);
                }

                // Analyze connection requirements
                let connectionMatching: ConnectionMatchingResult | undefined;
                if (validation.valid && validation.parsed) {
                  const { references } = useConnectionStore.getState();
                  const proposedWorkflow = validation.parsed as import('@/types/workflowDefinition').WorkflowDefinition;
                  connectionMatching = analyzeConnectionRequirements(
                    proposedWorkflow,
                    currentWorkflow || null,
                    references
                  );
                }

                const proposal: WorkflowProposal = {
                  id: event.proposal_id || crypto.randomUUID(),
                  type: 'workflow',
                  // For multi-node: use target_names from backend event, or fall back to editScope/currentMultiSelectionScope
                  ...(() => {
                    const names = event.target_names ?? editScope?.nodeNames ?? currentMultiSelectionScope?.nodeNames;
                    return names ? { targetNames: names } : {};
                  })(),
                  proposedJson: jsonContent,
                  baselineJson,
                  diff,
                  status: scopeValidationError ? 'invalid' : !diff.hasChanges ? 'no-change' : 'pending',
                  timestamp: Date.now(),
                  messageId: event.message_id || `assistant-${Date.now()}`,
                  ...(requestWorkflowIdentity ? { workflowIdentity: requestWorkflowIdentity } : {}),
                  ...(requestWorkflowFallbackKey ? { workflowFallbackKey: requestWorkflowFallbackKey } : {}),
                  ...(scopeValidationError && { validationError: scopeValidationError }),
                  ...(connectionMatching && {
                    connectionMatching,
                    connectionsResolved: connectionMatching.needsApproval.length === 0,
                  }),
                };

                get().addProposal(proposal);
              }

              // Accumulate a note about the proposal for API history
              set((state) => ({
                currentContent: state.currentContent
                  ? `${state.currentContent}\n[Generated workflow JSON proposal]`
                  : '[Generated workflow JSON proposal]'
              }));
            }
          } else if (event.type === 'no_changes') {
            // If a proposal was already processed in this streaming session,
            // the "no changes" event is stale (from an earlier agent iteration) — skip it.
            if (proposalAlreadyHandled) {
              console.log('[AG-UI] Skipping no_changes event — proposal already handled in this session');
              return;
            }

            // No workflow changes were generated (may be revised if a proposal arrives later)
            const noChangesText = event.reason || 'No workflow changes generated';
            const noChangesId = `chat-nochanges-${Date.now()}`;
            noChangesMessageIds.push(noChangesId);

            const message: ChatMessage = {
              id: noChangesId,
              role: 'assistant',
              content: noChangesText,
              timestamp: Date.now()
            };
            const assistantNoChangesMessage: AguiMessage = {
              id: `assistant-nochanges-${Date.now()}`,
              role: 'assistant',
              content: message.content,
              timestamp: Date.now(),
            };
            set((state) => ({
              chatMessages: [...state.chatMessages, message],
              messageHistory: [...state.messageHistory, assistantNoChangesMessage],
              currentStatus: null,
              // Accumulate for API history
              currentContent: state.currentContent
                ? `${state.currentContent}\n${noChangesText}`
                : noChangesText
            }));
          } else if (event.type === 'assistant_message') {
            // Filtered AI message (question, error, warning, info)
            const messageContent = event.content || '';

            // Skip AI-generated "Required Connections" messages ONLY if that's the main content
            // Don't filter if it's just mentioned within a larger explanation
            const isOnlyConnectionsMessage = messageContent.trim().startsWith('Required Connection') || 
              (messageContent.includes('Required Connections') && messageContent.length < 500);
            
            if (isOnlyConnectionsMessage) {
              console.log('[AG-UI] Filtering out AI-generated Required Connections message');
              return;
            }

            if (messageContent) {
              // Deduplicate: Check if the last message has the same content (prevents duplicate warnings)
              // Use normalized comparison to catch slight whitespace differences
              const currentMessages = get().chatMessages;
              const lastMessage = currentMessages[currentMessages.length - 1];
              const normalizedContent = messageContent.trim().replace(/\s+/g, ' ');
              const lastNormalized = lastMessage?.content?.trim().replace(/\s+/g, ' ');
              
              // Also check if both start with the warning emoji (same type of warning)
              const isWarning = normalizedContent.startsWith('⚠️');
              const lastIsWarning = lastNormalized?.startsWith('⚠️');
              
              if (lastMessage && lastMessage.role === 'assistant') {
                // Exact match after normalization
                if (lastNormalized === normalizedContent) {
                  console.log('[AG-UI] Skipping duplicate assistant message (exact match)');
                  return;
                }
                // Both are "Cannot edit that node" warnings - skip duplicate
                if (isWarning && lastIsWarning && 
                    normalizedContent.includes('Cannot edit that node') && 
                    lastNormalized?.includes('Cannot edit that node')) {
                  console.log('[AG-UI] Skipping duplicate "Cannot edit" warning');
                  return;
                }
              }

              console.log('[AG-UI] Adding assistant message to chat, length:', messageContent.length);
              const chatMessage: ChatMessage = {
                id: `chat-assistant-${Date.now()}`,
                role: 'assistant',
                content: messageContent,
                timestamp: Date.now()
              };
              const assistantHistoryMessage: AguiMessage = {
                id: `assistant-structured-${Date.now()}`,
                role: 'assistant',
                content: messageContent,
                timestamp: Date.now(),
              };
              set((state) => ({
                chatMessages: [...state.chatMessages, chatMessage],
                messageHistory: [...state.messageHistory, assistantHistoryMessage],
                currentStatus: null,
                // Accumulate for API history
                currentContent: state.currentContent
                  ? `${state.currentContent}\n${messageContent}`
                  : messageContent
              }));
            }
          } else if (event.type === 'run_finished') {
            // Capture session ID for reuse in subsequent requests
            if (event.session_id) {
              set({ copilotSessionId: event.session_id });
            }

            // Snapshot any pipeline-stage breakdown collected during this turn
            // onto the most-recent assistant ChatMessage so a reloaded chat
            // shows the same Plan → Build → Validate cards the user
            // saw live. If no assistant message was emitted but stages were
            // collected, synthesize a placeholder message that carries them
            // (otherwise the breakdown would be lost on save).
            const finishedState = get();
            const stageMap = finishedState.currentTurnStages;
            const stageList = Object.values(stageMap)
              .filter((s): s is StageEventSnapshot => !!s)
              .sort((a, b) => a.timestamp - b.timestamp);

            if (stageList.length > 0) {
              set((state) => {
                const messages = [...state.chatMessages];
                // Attach stages to the FIRST assistant message in this turn
                // so they render at the top of the turn's output (above
                // builder narrative, warnings, proposal hints, etc.).
                for (let i = turnStartIndex; i < messages.length; i++) {
                  if (messages[i]?.role === 'assistant') {
                    messages[i] = { ...messages[i]!, stages: stageList };
                    return { chatMessages: messages, currentTurnStages: {} };
                  }
                }
                // No assistant message produced this turn — synthesize a
                // stages-only placeholder.
                messages.push({
                  id: `chat-stages-${Date.now()}`,
                  role: 'assistant',
                  content: '',
                  timestamp: Date.now(),
                  stages: stageList,
                });
                return { chatMessages: messages, currentTurnStages: {} };
              });
            } else {
              set({ currentTurnStages: {} });
            }

            // Clear streaming state (hint message is added in addProposal)
            set({
              isStreaming: false,
              feedbackEligible: true,
              abortController: null,
              currentStatus: null
            });
          }
        },
        onError: (error: Error) => {
          const userFriendlyError = sanitizeErrorMessage(error.message);
          telemetryService.trackEvent('agent.chat.error', { error: userFriendlyError });
          const errorMessage: ChatMessage = {
            id: `chat-error-${Date.now()}`,
            role: 'assistant',
            content: `❌ Error: ${userFriendlyError}`,
            timestamp: Date.now()
          };
          // Snapshot any stages collected up to the failure point onto the
          // error message so the user (and chat history) can still see how
          // far the pipeline got. Clearing happens here too to avoid stale
          // state leaking into the next turn.
          set((state) => {
            const collectedStages = Object.values(state.currentTurnStages)
              .filter((s): s is StageEventSnapshot => !!s)
              .sort((a, b) => a.timestamp - b.timestamp);
            if (collectedStages.length > 0) {
              errorMessage.stages = collectedStages;
            }
            return {
              error: userFriendlyError,
              isStreaming: false,
              abortController: null,
              currentStatus: null,
              currentTurnStages: {},
              chatMessages: [...state.chatMessages, errorMessage]
            };
          });
        }
      });

      // Ensure streaming is stopped after stream completes (fallback if run_finished not received)
      const currentState = get();
      if (currentState.isStreaming) {
        // Clear streaming state. If stages were collected but run_finished
        // never fired, attach them to the most-recent assistant message in
        // THIS turn so they aren't lost.
        const collectedStages = Object.values(currentState.currentTurnStages)
          .filter((s): s is StageEventSnapshot => !!s)
          .sort((a, b) => a.timestamp - b.timestamp);
        if (collectedStages.length > 0) {
          set((state) => {
            const messages = [...state.chatMessages];
            for (let i = turnStartIndex; i < messages.length; i++) {
              if (messages[i]?.role === 'assistant') {
                messages[i] = { ...messages[i]!, stages: collectedStages };
                return {
                  chatMessages: messages,
                  currentTurnStages: {},
                  isStreaming: false,
                  feedbackEligible: true,
                  abortController: null,
                  currentStatus: null,
                };
              }
            }
            messages.push({
              id: `chat-stages-${Date.now()}`,
              role: 'assistant',
              content: '',
              timestamp: Date.now(),
              stages: collectedStages,
            });
            return {
              chatMessages: messages,
              currentTurnStages: {},
              isStreaming: false,
              feedbackEligible: true,
              abortController: null,
              currentStatus: null,
            };
          });
        } else {
          set({
            isStreaming: false,
            feedbackEligible: true,
            abortController: null,
            currentStatus: null,
            currentTurnStages: {},
          });
        }
      }
    } catch (error: unknown) {
      if (error instanceof Error && error.name !== 'AbortError') {
        const userFriendlyError = sanitizeErrorMessage(error.message);
        telemetryService.trackEvent('agent.chat.error', { error: userFriendlyError });
        const errorMessage: ChatMessage = {
          id: `chat-error-${Date.now()}`,
          role: 'assistant',
          content: `❌ Error: ${userFriendlyError}`,
          timestamp: Date.now()
        };
        set((state) => {
          const collectedStages = Object.values(state.currentTurnStages)
            .filter((s): s is StageEventSnapshot => !!s)
            .sort((a, b) => a.timestamp - b.timestamp);
          if (collectedStages.length > 0) {
            errorMessage.stages = collectedStages;
          }
          return {
            error: userFriendlyError,
            isStreaming: false,
            abortController: null,
            currentStatus: null,
            currentTurnStages: {},
            chatMessages: [...state.chatMessages, errorMessage]
          };
        });
      } else {
        // AbortError - handled in cancelStream
      }
    }
  },

  handleToolExecutionRequest: async (request: ToolExecutionRequest, getToken: () => Promise<string | null>, azureContext: AzureContext) => {
    const projectName = useAppStore.getState().selectedProject ?? '';
    const ottoAccessToken = await getToken();

    // Helper to clean up pending execution
    const cleanupPending = () => {
      set((state) => {
        const newPending = new Map(state.pendingToolExecutions);
        newPending.delete(request.execution_id);
        return { pendingToolExecutions: newPending };
      });
    };

    try {
      const selectedApp = useAppStore.getState().selectedApp;
      const selectedProject = selectedApp?.projectName;

      let content: string;
      let isError = false;

      // Handle ListKnowledgeHubs directly — needs project context and dataplane token
      if (request.tool_name === 'ListKnowledgeHubs') {
        if (selectedApp?.projectName) {
          if (ottoAccessToken) {
            const hubs = await listKnowledgeHubs(ottoAccessToken, selectedApp.projectName);
            content = JSON.stringify({
              knowledgeHubs: hubs.map(h => ({
                name: h.name,
                sourceType: h.properties.sourceType ?? h.type,
                status: h.properties.status,
                description: h.properties.description,
              })),
              total: hubs.length,
            });
          } else {
            content = 'Error: Failed to acquire Otto dataplane token. Please sign in again and retry.';
            isError = true;
          }
        } else {
          content = JSON.stringify({ knowledgeHubs: [], total: 0, note: 'No project context available' });
        }
        await postToolResult(projectName, ottoAccessToken ?? '', { execution_id: request.execution_id, content, is_error: isError });
        cleanupPending();
        return;
      }

      // Execute tool using Azure context from selected app
      const executor = new AzureToolExecutor(
        ottoAccessToken || '',  // Empty string for non-auth tools (GetWorkflowSchema)
        selectedProject ?? '',
        selectedApp?.name ?? '',
        azureContext.workflowName,
      );
      const result = await executor.executeTool(request);

      // Post result back to backend
      await postToolResult(projectName, ottoAccessToken ?? '', result);
    } catch (error: unknown) {
      console.error('[AG-UI] Tool execution failed:', error);
      await postToolResult(projectName, ottoAccessToken ?? '', {
        execution_id: request.execution_id,
        content: error instanceof Error ? error.message : 'Tool execution failed',
        is_error: true
      });
    } finally {
      // Always clean up pending execution
      cleanupPending();
    }
  },

  cancelStream: () => {
    const { abortController } = get();
    if (abortController) {
      abortController.abort();
      const cancelMessage: ChatMessage = {
        id: `chat-cancel-${Date.now()}`,
        role: 'assistant',
        content: 'Cancelled',
        timestamp: Date.now()
      };
      // Preserve any stages collected before cancel so the user sees how far
      // the cancelled turn got, and clear currentTurnStages to prevent stale
      // state from leaking into the next turn.
      set((state) => {
        const collectedStages = Object.values(state.currentTurnStages)
          .filter((s): s is StageEventSnapshot => !!s)
          .sort((a, b) => a.timestamp - b.timestamp);
        if (collectedStages.length > 0) {
          cancelMessage.stages = collectedStages;
        }
        return {
          isStreaming: false,
          feedbackEligible: false,
          abortController: null,
          currentContent: '',
          currentStatus: null,
          currentTurnStages: {},
          chatMessages: [...state.chatMessages, cancelMessage]
        };
      });
    }
  },

  clearChat: () => {
    set({
      chatMessages: [],
      currentStatus: null,
      currentTurnStages: {},
      pendingClarification: null,
      messageHistory: [],
      error: null,
      currentContent: '',
      isStreaming: false,
      feedbackEligible: false,
      pendingProposals: new Map(),
      currentChatId: null, // Clear current chat session
      contextUsagePercent: null,
      contextStatus: null,
      copilotSessionId: null,
    });
  },

  clearChatMessages: () => {
    // Clear messages but preserve session IDs for continuity
    set({
      chatMessages: [],
      currentStatus: null,
      currentTurnStages: {},
      pendingClarification: null,
      messageHistory: [],
      error: null,
      currentContent: '',
      isStreaming: false,
      feedbackEligible: false,
      pendingProposals: new Map(),
      contextUsagePercent: null,
      contextStatus: null,
      // Keep currentChatId and copilotSessionId to preserve session
    });
  },

  // Chat history persistence actions
  saveCurrentChat: async (workflowName: string, opts?: ChatHistoryFetchOptions) => {
    const state = get();
    
    // Don't save empty chats
    if (state.chatMessages.length === 0) {
      return;
    }

    try {
      // Convert chat messages to conversation format.
      // Assistant messages with a `stages` or `clarification` snapshot
      // attach the combined metadata as JSON in the optional `metadata`
      // field so a reloaded chat shows the same Plan → Build →
      // Validate cards AND any clarification card (with its answer
      // highlighted) the user saw live. Backend round-trips this
      // opaquely; only the frontend interprets it.
      const messages = state.chatMessages
        .filter(m => m.role === 'user' || m.role === 'assistant')
        .map(m => {
          if (m.role === 'assistant' && (m.stages?.length || m.clarification || m.feedback)) {
            const meta: AssistantTurnMetadata = {
              v: 1,
              ...(m.stages?.length ? { stages: m.stages } : {}),
              ...(m.clarification ? { clarification: m.clarification } : {}),
              ...(m.feedback ? { feedback: m.feedback } : {}),
            };
            return { role: m.role, content: m.content, metadata: JSON.stringify(meta) };
          }
          return { role: m.role, content: m.content };
        });

      const response = await chatHistoryClient.saveChat(
        workflowName,
        messages,
        state.currentChatId ?? undefined,
        opts,
        {
          ...(state.copilotSessionId ? { copilotSessionId: state.copilotSessionId } : {}),
          contextUsagePercent: state.contextUsagePercent,
        }
      );

      // Update current chat ID if this was a new chat and increment version to trigger list refresh
      set((s) => ({
        currentChatId: s.currentChatId ?? response.id,
        chatHistoryVersion: s.chatHistoryVersion + 1,
      }));

      console.log('[Chat History] Saved chat:', response.id, response.name);
    } catch (error) {
      console.error('[Chat History] Failed to save chat:', error);
      // Don't set error state - this is a background operation
    }
  },

  loadChat: async (workflowName: string, chatId: string, opts?: ChatHistoryFetchOptions) => {
    try {
      const savedChat = await chatHistoryClient.getChat(workflowName, chatId, opts);

      // Convert saved messages back to chat message format. Assistant
      // messages with a `metadata` JSON blob are parsed back into their
      // pipeline-stage breakdown so the user sees the same collapsible
      // cards they saw live.
      const chatMessages = savedChat.messages.map((m, idx) => {
        const base: ChatMessage = {
          id: `loaded-${chatId}-${idx}`,
          role: m.role as 'user' | 'assistant',
          content: m.content,
          timestamp: new Date(savedChat.createdAt).getTime() + idx,
        };
        if (m.role === 'assistant' && m.metadata) {
          try {
            const parsed = JSON.parse(m.metadata) as AssistantTurnMetadata;
            if (parsed?.v === 1) {
              if (Array.isArray(parsed.stages) && parsed.stages.length > 0) {
                base.stages = parsed.stages;
              }
              if (parsed.clarification) {
                base.clarification = parsed.clarification;
              }
              if (parsed.feedback) {
                base.feedback = parsed.feedback;
              }
            }
          } catch {
            // Older or malformed metadata — silently drop, don't fail the load.
          }
        }
        return base;
      });

      // Also build message history for context
      const messageHistory = savedChat.messages.map((m, idx) => ({
        id: `history-${chatId}-${idx}`,
        role: m.role as 'user' | 'assistant',
        content: m.content,
        timestamp: new Date(savedChat.createdAt).getTime() + idx,
      }));

      set({
        chatMessages,
        messageHistory,
        currentChatId: chatId,
        currentStatus: null,
        currentTurnStages: {},
        pendingClarification: null,
        error: null,
        isStreaming: false,
        feedbackEligible: false,
        pendingProposals: new Map(),
        copilotSessionId: savedChat.copilotSessionId ?? null,
        contextUsagePercent: savedChat.contextUsagePercent ?? null,
        contextStatus: savedChat.contextUsagePercent != null
          ? (savedChat.contextUsagePercent >= 0.6 ? 'warning' : 'normal')
          : null,
        // Keep history panel open - user should close it intentionally
      });

      console.log('[Chat History] Loaded chat:', chatId, savedChat.name);
    } catch (error) {
      console.error('[Chat History] Failed to load chat:', error);
      set({ error: `Failed to load chat: ${error instanceof Error ? error.message : 'Unknown error'}` });
    }
  },

  setCurrentChatId: (chatId: string | null) => {
    set({ currentChatId: chatId });
  },

  toggleHistoryPanel: () => {
    set((state) => ({ isHistoryPanelOpen: !state.isHistoryPanelOpen }));
  },

  openHistoryPanel: () => {
    set({ isHistoryPanelOpen: true });
  },

  closeHistoryPanel: () => {
    set({ isHistoryPanelOpen: false });
  },

  setHasUnreadMessages: (hasUnread: boolean) => {
    set({ hasUnreadMessages: hasUnread });
  },

  queuePrompt: (content: string, scope: StickyEditScope, source: CopilotEntryPointSource, systemContext?: string) => {
    const pendingPrompt: { content: string; scope: StickyEditScope; source: CopilotEntryPointSource; systemContext?: string } = {
      content,
      scope,
      source
    };

    if (systemContext !== undefined) {
      pendingPrompt.systemContext = systemContext;
    }

    set({ pendingPrompt });

    // Persist to sessionStorage as fallback for page refresh during navigation
    try {
      sessionStorage.setItem('otto:pendingPrompt', JSON.stringify({ ...pendingPrompt, storedAt: Date.now() }));
    } catch { /* Private browsing or quota — Zustand still works for SPA nav */ }
  },

  clearPendingPrompt: () => {
    set({ pendingPrompt: null });
    try { sessionStorage.removeItem('otto:pendingPrompt'); } catch { /* sessionStorage unavailable */ }
  },

  answerClarification: async (text, optionId, getToken, azureContext) => {
    const { pendingClarification } = get();
    if (!pendingClarification) {
      return;
    }
    if (!text || !text.trim()) {
      return; // No empty replies — the UI also disables the send button.
    }

    // Mark the snapshot as answered so the persisted clarification card
    // can render the highlighted choice on reload. Mutate via chatMessages
    // so the change participates in the next saveCurrentChat.
    set((state) => {
      const messages = state.chatMessages.map((m) => {
        if (m.role === 'assistant' && m.clarification?.messageId === pendingClarification.messageId) {
          return {
            ...m,
            clarification: {
              ...m.clarification,
              answer: {
                ...(optionId ? { optionId } : {}),
                sentText: text.trim(),
              },
            },
          };
        }
        return m;
      });
      return { chatMessages: messages, pendingClarification: null };
    });

    // Dispatch the answer via the normal sendMessage path so the user's
    // reply enters chat history as a regular turn and the assistant
    // continues the conversation with full context.
    await get().sendMessage(text.trim(), getToken, azureContext);
  },

  // Proposal management actions
  addProposal: (proposal: WorkflowProposal) => {
    // A proposal is a completed, rateable assistant output. Proposal event flows
    // often skip text_message_end/assistant_message (and sometimes run_finished),
    // so mark the turn feedback-eligible here rather than relying on those.
    if (proposal.status === 'pending') {
      const hintMessage: ChatMessage = {
        id: `chat-proposal-hint-${Date.now()}`,
        role: 'assistant',
        content: 'Proposed changes ready for review. Please approve or reject below.',
        timestamp: proposal.timestamp - 1 // Ensures message appears before proposal
      };
      set((state) => ({
        chatMessages: [...state.chatMessages, hintMessage],
        pendingProposals: new Map(state.pendingProposals).set(proposal.id, proposal),
        feedbackEligible: true,
      }));
    } else {
      set((state) => ({
        pendingProposals: new Map(state.pendingProposals).set(proposal.id, proposal),
        feedbackEligible: true,
      }));
    }
  },

  approveProposal: async (id: string) => {
    const proposal = get().pendingProposals.get(id);
    if (!proposal) {
      console.warn('[AG-UI] Proposal not found:', id);
      return;
    }

    if (proposal.status !== 'pending') {
      console.warn('[AG-UI] Proposal is not pending:', id, proposal.status);
      return;
    }

    // Don't apply a proposal to a different workflow than the one it was
    // generated for — the user may have switched workflows since (issue #2154).
    if (!isActiveWorkflow(proposal.workflowIdentity, proposal.workflowFallbackKey)) {
      console.warn('[AG-UI] Proposal targets a different workflow than the active one — skipping apply', id);
      set((state) => ({
        error: 'Those changes were generated for a different workflow. Switch back to that workflow to apply them.',
        chatMessages: [...state.chatMessages, buildWorkflowSwitchedMessage(proposal.workflowIdentity)],
      }));
      return;
    }

    // Session history auto-captures changes via store subscriptions

    // Apply the changes based on proposal type
    let success = false;
    if (proposal.type === 'workflow') {
      // Apply connection assignments to workflow before applying
      let workflowJson = proposal.proposedJson;
      if (proposal.connectionMatching) {
        try {
          const parsed = JSON.parse(proposal.proposedJson);
          const updated = applyConnectionAssignments(parsed, proposal.connectionMatching);
          workflowJson = JSON.stringify(updated, null, 2);
        } catch (e) {
          console.warn('[AG-UI] Failed to apply connection assignments:', e);
        }
      }
      success = applyWorkflowToCanvas(workflowJson);
    } else if (proposal.targetPath) {
      success = applyAtPath(proposal.targetPath, proposal.proposedJson);
    }

    if (success) {
      // Update proposal status and mark all changes as approved
      const updatedSummary = proposal.diff.summary.map((change) => ({
        ...change,
        status: 'approved' as const,
      }));
      const updatedProposal = {
        ...proposal,
        status: 'approved' as const,
        diff: { ...proposal.diff, summary: updatedSummary },
      };
      set((state) => ({
        pendingProposals: new Map(state.pendingProposals).set(id, updatedProposal)
      }));

      // Add confirmation message to chat
      const entityDesc = proposal.type === 'workflow'
        ? 'Workflow'
        : `"${proposal.targetName}"`;

      const newMessages: ChatMessage[] = [];

      // Add confirmation message
      newMessages.push({
        id: `chat-approved-${Date.now()}`,
        role: 'assistant',
        content: `✅ ${entityDesc} changes approved and applied.`,
        timestamp: Date.now()
      });

      // Add required connections message for actions without available connections
      const connectionsMessage = generateRequiredConnectionsMessage(proposal.connectionMatching);
      if (connectionsMessage) {
        newMessages.push({
          id: `chat-connections-${Date.now()}`,
          role: 'assistant',
          content: connectionsMessage,
          timestamp: Date.now() + 1
        });
      }

      set((state) => ({
        chatMessages: [...state.chatMessages, ...newMessages]
      }));
    } else {
      set({ error: 'Failed to apply approved changes' });
    }
  },

  rejectProposal: (id: string, reason?: string) => {
    const proposal = get().pendingProposals.get(id);
    if (!proposal) {
      console.warn('[AG-UI] Proposal not found:', id);
      return;
    }

    // Update proposal status
    const updatedProposal = { ...proposal, status: 'rejected' as const };
    set((state) => ({
      pendingProposals: new Map(state.pendingProposals).set(id, updatedProposal)
    }));

    // Add rejection message to chat
    const entityDesc = proposal.type === 'workflow'
      ? 'Workflow'
      : `"${proposal.targetName}"`;

    const rejectMessage: ChatMessage = {
      id: `chat-rejected-${Date.now()}`,
      role: 'assistant',
      content: `❌ ${entityDesc} changes rejected.${reason ? ` Reason: ${reason}` : ''} You can ask me to modify the proposal.`,
      timestamp: Date.now()
    };

    set((state) => ({
      chatMessages: [...state.chatMessages, rejectMessage]
    }));
  },

  clearProposal: (id: string) => {
    set((state) => {
      const newProposals = new Map(state.pendingProposals);
      newProposals.delete(id);
      return { pendingProposals: newProposals };
    });
  },

  getProposal: (id: string) => {
    return get().pendingProposals.get(id);
  },

  // Individual change actions
  approveChange: (proposalId: string, changeId: string) => {
    const proposal = get().pendingProposals.get(proposalId);
    if (!proposal) return;

    const updatedSummary = proposal.diff.summary.map((change) =>
      change.id === changeId ? { ...change, status: 'approved' as const } : change
    );

    const updatedProposal = {
      ...proposal,
      diff: { ...proposal.diff, summary: updatedSummary },
    };

    set((state) => ({
      pendingProposals: new Map(state.pendingProposals).set(proposalId, updatedProposal),
    }));
  },

  rejectChange: (proposalId: string, changeId: string) => {
    const proposal = get().pendingProposals.get(proposalId);
    if (!proposal) return;

    const updatedSummary = proposal.diff.summary.map((change) =>
      change.id === changeId ? { ...change, status: 'rejected' as const } : change
    );

    const updatedProposal = {
      ...proposal,
      diff: { ...proposal.diff, summary: updatedSummary },
    };

    set((state) => ({
      pendingProposals: new Map(state.pendingProposals).set(proposalId, updatedProposal),
    }));
  },

  undoChangeDecision: (proposalId: string, changeId: string) => {
    const proposal = get().pendingProposals.get(proposalId);
    if (!proposal) return;

    const updatedSummary = proposal.diff.summary.map((change) =>
      change.id === changeId ? { ...change, status: 'pending' as const } : change
    );

    const updatedProposal = {
      ...proposal,
      diff: { ...proposal.diff, summary: updatedSummary },
    };

    set((state) => ({
      pendingProposals: new Map(state.pendingProposals).set(proposalId, updatedProposal),
    }));
  },

  approveAllChanges: (proposalId: string) => {
    const proposal = get().pendingProposals.get(proposalId);
    if (!proposal) return;

    const updatedSummary = proposal.diff.summary.map((change) => ({
      ...change,
      status: 'approved' as const,
    }));

    const updatedProposal = {
      ...proposal,
      diff: { ...proposal.diff, summary: updatedSummary },
    };

    set((state) => ({
      pendingProposals: new Map(state.pendingProposals).set(proposalId, updatedProposal),
    }));
  },

  rejectAllChanges: (proposalId: string) => {
    const proposal = get().pendingProposals.get(proposalId);
    if (!proposal) return;

    const updatedSummary = proposal.diff.summary.map((change) => ({
      ...change,
      status: 'rejected' as const,
    }));

    const updatedProposal = {
      ...proposal,
      diff: { ...proposal.diff, summary: updatedSummary },
    };

    set((state) => ({
      pendingProposals: new Map(state.pendingProposals).set(proposalId, updatedProposal),
    }));
  },

  applyApprovedChanges: async (proposalId: string) => {
    const proposal = get().pendingProposals.get(proposalId);
    if (!proposal) {
      console.warn('[AG-UI] Proposal not found:', proposalId);
      return;
    }

    // Get only approved changes
    const approvedChanges = proposal.diff.summary.filter((c) => c.status === 'approved');

    if (approvedChanges.length === 0) {
      // No changes approved - treat as rejection
      get().rejectProposal(proposalId, 'No changes were approved');
      return;
    }

    // Don't apply a proposal to a different workflow than the one it was
    // generated for — the user may have switched workflows since (issue #2154).
    if (!isActiveWorkflow(proposal.workflowIdentity, proposal.workflowFallbackKey)) {
      console.warn('[AG-UI] Proposal targets a different workflow than the active one — skipping apply', proposalId);
      set((state) => ({
        error: 'Those changes were generated for a different workflow. Switch back to that workflow to apply them.',
        chatMessages: [...state.chatMessages, buildWorkflowSwitchedMessage(proposal.workflowIdentity)],
      }));
      return;
    }

    // Session history auto-captures changes via store subscriptions

    // Apply approved changes by building a partial workflow
    const currentWorkflow = useWorkflowStore.getState().workflow;
    if (!currentWorkflow) {
      set({ error: 'No workflow loaded' });
      return;
    }

    try {
      let success = false;

      if (proposal.type !== 'workflow' && proposal.targetPath) {
        // For scoped proposals, diff paths are relative to the scoped entity.
        // Build the merged entity by applying approved changes to the baseline, then write at the correct path.
        const baselineObj = JSON.parse(proposal.baselineJson || '{}');

        for (const change of approvedChanges) {
          const pathParts = change.path.split('.');
          applyChangeAtPath(baselineObj, pathParts, change);
        }

        const mergedJson = JSON.stringify(baselineObj, null, 2);
        if (proposal.targetPath) {
          success = applyAtPath(proposal.targetPath, mergedJson);
        }
      } else {
        // For workflow-level proposals, diff paths are relative to the full workflow object.
        // Start with current workflow and apply only approved changes
        const mergedWorkflow = JSON.parse(JSON.stringify(currentWorkflow));

        for (const change of approvedChanges) {
          const pathParts = change.path.split('.');
          applyChangeAtPath(mergedWorkflow, pathParts, change);
        }

        // Apply connection assignments to workflow before applying
        const workflowToApply = proposal.connectionMatching
          ? applyConnectionAssignments(mergedWorkflow, proposal.connectionMatching)
          : mergedWorkflow;

        // Apply the merged workflow
        const result = useWorkflowStore.getState().updateWorkflowFromJson(
          JSON.stringify(workflowToApply, null, 2)
        );
        success = result.success;
        if (!result.success) {
          console.error('[AG-UI] Failed to apply workflow:', result.error);
        }
      }

      if (success) {
        // Update proposal status
        const updatedProposal = { ...proposal, status: 'approved' as const };
        set((state) => ({
          pendingProposals: new Map(state.pendingProposals).set(proposalId, updatedProposal),
        }));

        // Add required connections message for actions without available connections
        const connectionsMessage = generateRequiredConnectionsMessage(proposal.connectionMatching);
        if (connectionsMessage) {
          set((state) => ({
            chatMessages: [...state.chatMessages, {
              id: `chat-connections-${Date.now()}`,
              role: 'assistant' as const,
              content: connectionsMessage,
              timestamp: Date.now(),
            }],
          }));
        }
      } else {
        set({ error: 'Failed to apply approved changes' });
      }
    } catch (error) {
      console.error('[AG-UI] Error applying approved changes:', error);
      set({ error: 'Failed to apply approved changes' });
    }
  },

  // Connection assignment actions
  approveConnectionAssignment: (proposalId: string, requirementId: string, selectedReferenceKey: string) => {
    const proposal = get().pendingProposals.get(proposalId);
    if (!proposal?.connectionMatching) return;

    const updatedNeedsApproval = proposal.connectionMatching.needsApproval.map((p) => {
      if (p.id === requirementId) {
        const selectedConnection = p.compatibleConnections.find(c => c.referenceKey === selectedReferenceKey) || null;
        return { ...p, selectedConnection, status: 'approved' as const };
      }
      return p;
    });

    const updatedConnectionMatching = {
      ...proposal.connectionMatching,
      needsApproval: updatedNeedsApproval,
    };

    // Check if all connections are resolved
    const allResolved = updatedNeedsApproval.every(p => p.status !== 'pending');

    const updatedProposal = {
      ...proposal,
      connectionMatching: updatedConnectionMatching,
      connectionsResolved: allResolved,
    };

    set((state) => ({
      pendingProposals: new Map(state.pendingProposals).set(proposalId, updatedProposal),
    }));
  },

  rejectConnectionAssignment: (proposalId: string, requirementId: string) => {
    const proposal = get().pendingProposals.get(proposalId);
    if (!proposal?.connectionMatching) return;

    const updatedNeedsApproval = proposal.connectionMatching.needsApproval.map((p) => {
      if (p.id === requirementId) {
        return { ...p, selectedConnection: null, status: 'rejected' as const };
      }
      return p;
    });

    const updatedConnectionMatching = {
      ...proposal.connectionMatching,
      needsApproval: updatedNeedsApproval,
    };

    // Check if all connections are resolved
    const allResolved = updatedNeedsApproval.every(p => p.status !== 'pending');

    const updatedProposal = {
      ...proposal,
      connectionMatching: updatedConnectionMatching,
      connectionsResolved: allResolved,
    };

    set((state) => ({
      pendingProposals: new Map(state.pendingProposals).set(proposalId, updatedProposal),
    }));
  },

  approveAllConnectionAssignments: (proposalId: string) => {
    const proposal = get().pendingProposals.get(proposalId);
    if (!proposal?.connectionMatching) return;

    const updatedNeedsApproval = proposal.connectionMatching.needsApproval.map((p) => {
      if (p.status === 'pending' && p.compatibleConnections.length > 0) {
        // Auto-select first compatible connection
        const firstConnection = p.compatibleConnections[0] ?? null;
        return { ...p, selectedConnection: firstConnection, status: 'approved' as const };
      }
      return p;
    });

    const updatedConnectionMatching = {
      ...proposal.connectionMatching,
      needsApproval: updatedNeedsApproval,
    };

    const updatedProposal = {
      ...proposal,
      connectionMatching: updatedConnectionMatching,
      connectionsResolved: true,
    };

    set((state) => ({
      pendingProposals: new Map(state.pendingProposals).set(proposalId, updatedProposal),
    }));
  },

  skipAllConnectionAssignments: (proposalId: string) => {
    const proposal = get().pendingProposals.get(proposalId);
    if (!proposal?.connectionMatching) return;

    const updatedNeedsApproval = proposal.connectionMatching.needsApproval.map((p) => {
      if (p.status === 'pending') {
        return { ...p, selectedConnection: null, status: 'rejected' as const };
      }
      return p;
    });

    const updatedConnectionMatching = {
      ...proposal.connectionMatching,
      needsApproval: updatedNeedsApproval,
    };

    const updatedProposal = {
      ...proposal,
      connectionMatching: updatedConnectionMatching,
      connectionsResolved: true,
    };

    set((state) => ({
      pendingProposals: new Map(state.pendingProposals).set(proposalId, updatedProposal),
    }));
  },
}));

/**
 * Apply a single change at a specific path in the workflow object
 */
function applyChangeAtPath(
  obj: Record<string, unknown>,
  pathParts: string[],
  change: { type: 'added' | 'removed' | 'modified'; newValue?: unknown }
): void {
  if (pathParts.length === 0) return;

  let current: Record<string, unknown> = obj;

  // Navigate to parent of the target path
  for (let i = 0; i < pathParts.length - 1; i++) {
    const key = pathParts[i]!;
    if (current[key] === undefined) {
      if (change.type === 'removed') return; // Nothing to remove
      current[key] = {};
    }
    current = current[key] as Record<string, unknown>;
  }

  const finalKey = pathParts[pathParts.length - 1]!;

  if (change.type === 'removed') {
    delete current[finalKey];
  } else {
    // added or modified
    current[finalKey] = change.newValue;
  }
}

/**
 * Extract workflow JSON from message content
 */
export function extractWorkflowJson(content: string): string | null {
  const codeBlockRegex = /```json\s*([\s\S]*?)```/;
  const match = content.match(codeBlockRegex);
  if (!match || !match[1]) return null;

  try {
    const json = JSON.parse(match[1]);
    // Validate it looks like a workflow
    if (json.definition) return match[1];
  } catch {
    return null;
  }
  return null;
}

/** Warn the user about ForEach structural constraint violations (single-action limit / nesting). */
function notifyForEachConstraintWarnings(errors: string[]): void {
  useNotificationStore.getState().addNotification({
    type: 'warning',
    title: FOREACH_ACTION_LIMIT_TITLE,
    message: errors.join('. '),
  });
}

/**
 * Coarse project/app/workflow key used as a fallback for the full
 * `WorkflowIdentity` composite key. The full identity also includes
 * `userId`/`tenantId`, which hydrate asynchronously in the designer; those two
 * are constant across a mid-stream workflow switch, so dropping them still lets
 * us reliably detect that the *workflow* changed. Normalised (trimmed +
 * lowercased) so casing differences between the route params and the app-store
 * values don't cause false mismatches.
 */
export function composeWorkflowFallbackKey(
  project: string,
  app: string,
  workflow: string,
): string {
  return [project, app, workflow]
    .map((s) => encodeURIComponent((s ?? '').trim().toLowerCase()))
    .join('::');
}

/**
 * Guards assistant-driven writes against the "switched workflow mid-flight" bug
 * (issue #2154). `expected` is the workflow identity captured when the
 * request/turn began (or persisted on a proposal). `expectedFallbackKey` is a
 * coarse project/app/workflow key captured from `azureContext`/the app store at
 * the same moment — it is available even before the full identity hydrates.
 * Returns `true` when it is safe to apply changes to the workflow currently
 * loaded in the designer:
 *
 *  - a full `expected` identity was captured — compare it to the active
 *    workflow's identity by composite key (strong path).
 *  - no full identity, but a fallback key was captured — compare the fallback
 *    against the active workflow's coarse key once identity has hydrated. This
 *    keeps the guard working during the identity-hydration window, which would
 *    otherwise leave `expected` null and the guard disabled. If identity is
 *    STILL unhydrated at apply time we cannot prove the workflow is unchanged,
 *    so we fail safe and withhold (see below).
 *  - neither was captured (unit-test fixture / legacy) — we cannot prove a
 *    mismatch, so we allow the apply to preserve existing behaviour.
 *
 * Returns `false` when we KNOW — or, in the fallback path, cannot rule out —
 * that the active workflow differs from the one the change was generated for
 * (i.e. the user may have opened another workflow while the assistant worked).
 */
export function isActiveWorkflow(
  expected: WorkflowIdentity | null | undefined,
  expectedFallbackKey?: string | null,
): boolean {
  const current = useWorkflowStore.getState().currentWorkflowIdentity;

  // Strong path: a full identity was captured for this turn/proposal.
  if (expected) {
    if (!current) return false;
    return composeWorkflowKey(current) === composeWorkflowKey(expected);
  }

  // Fallback path: the full identity had not hydrated when the turn began, but
  // we captured a coarse project/app/workflow key. Once identity hydrates we
  // can still tell whether the active workflow is the one we started on.
  if (expectedFallbackKey) {
    // Identity is STILL null at apply time. We captured a fallback key, so a
    // specific workflow WAS open when the turn began — but `currentWorkflowIdentity`
    // is the only live signal for what's loaded now, and the designer leaves it
    // null until `userId` + `tenantId` resolve (it early-returns otherwise). With
    // no current identity we cannot prove the user hasn't switched workflows in
    // this window, so a guard against overwrites must fail safe and withhold.
    // In practice identity hydrates within ~1s of load, long before a multi-second
    // assistant turn completes, so this only trips in a degraded/never-authed state.
    if (!current) return false;
    return (
      composeWorkflowFallbackKey(current.project, current.app, current.workflow) ===
      expectedFallbackKey
    );
  }

  // No expectation captured at all — preserve existing behaviour.
  return true;
}

/**
 * Build the chat message shown when assistant changes are withheld because the
 * user switched workflows before they could be applied.
 */
function buildWorkflowSwitchedMessage(expected: WorkflowIdentity | null | undefined): ChatMessage {
  const target = expected?.workflow;
  const current = useWorkflowStore.getState().currentWorkflowIdentity?.workflow;
  const targetLabel = target ? `**${target}**` : 'another workflow';
  const currentLabel = current ? `**${current}**` : 'the workflow you have open now';
  return {
    id: `chat-workflow-switched-${Date.now()}`,
    role: 'assistant',
    content:
      `⚠️ I finished changes for ${targetLabel}, but you switched to ${currentLabel} ` +
      `while I was working. I didn't apply them so ${currentLabel} isn't overwritten. ` +
      `Switch back to ${targetLabel} and ask again to apply these changes.`,
    timestamp: Date.now(),
  };
}

/**
 * Apply workflow JSON to the workflow store. Node positions live in the
 * client-side layout store (keyed by workflow identity), so they survive
 * workflow JSON replacement automatically — no metadata.position merging
 * needed here.
 */
export function applyWorkflowToCanvas(workflowJson: string): boolean {
  try {
    const workflowStore = useWorkflowStore.getState();

    // Parse the new workflow
    let newWorkflow = JSON.parse(workflowJson);

    // Post-process: Patch any Http actions in Agent tools with discovered ApiConnection templates
    // This fixes cases where Claude generates Http actions instead of using the connector templates
    const { patched, fixedTools } = patchHttpActionsInAgentTools(newWorkflow);
    if (fixedTools.length > 0) {
      console.log(`[applyWorkflowToCanvas] Patched ${fixedTools.length} agent tools with ApiConnection:`, fixedTools);
      newWorkflow = patched;
    }

    // Validate ForEach constraints before applying
    const constraintResult = validateForEachConstraints(newWorkflow as Record<string, unknown>);
    if (!constraintResult.valid) {
      notifyForEachConstraintWarnings(constraintResult.errors);
      // Still apply — the store-level guard will block the actual violation
    }

    // Apply the merged workflow
    const result = workflowStore.updateWorkflowFromJson(JSON.stringify(newWorkflow, null, 2));
    if (result.success) {
      return true;
    } else {
      console.error('[AG-UI] Failed to apply workflow:', result.error);
      return false;
    }
  } catch (error) {
    console.error('[AG-UI] Error applying workflow to canvas:', error);
    return false;
  }
}

/**
 * Extract action JSON from message content (for scoped edits)
 */
export function extractActionJson(content: string): string | null {
  const codeBlockRegex = /```json\s*([\s\S]*?)```/;
  const match = content.match(codeBlockRegex);
  if (!match || !match[1]) return null;

  try {
    const json = JSON.parse(match[1]);
    // Validate it looks like an action (has type, but NOT definition which would be a workflow)
    if (json.type && !json.definition) return match[1];
  } catch {
    return null;
  }
  return null;
}

/**
 * Validate that a workflow proposal only modifies nodes within the multi-selection scope.
 * Returns a list of nodes that were modified but not in the allowed scope.
 * @param proposedWorkflow The proposed workflow JSON (parsed)
 * @param currentWorkflow The current workflow from the store
 * @param allowedNodeNames Array of node names that are allowed to be modified
 * @returns Object with isValid flag and list of out-of-scope modifications
 */
export function validateMultiSelectionScope(
  proposedWorkflow: import('@/types/workflowDefinition').WorkflowDefinition,
  currentWorkflow: import('@/types/workflowDefinition').WorkflowDefinition | null,
  allowedNodeNames: string[]
): { isValid: boolean; outOfScopeChanges: { type: 'action' | 'trigger'; name: string; changeType: 'added' | 'modified' | 'removed' }[] } {
  const outOfScopeChanges: { type: 'action' | 'trigger'; name: string; changeType: 'added' | 'modified' | 'removed' }[] = [];
  const allowedSet = new Set(allowedNodeNames);

  // Helper to check if two objects are deeply equal (simplified, handles JSON-serializable objects)
  const isEqual = (a: unknown, b: unknown): boolean => {
    return JSON.stringify(a) === JSON.stringify(b);
  };

  // Check proposed actions
  const proposedActions = proposedWorkflow.definition?.actions || {};
  const currentActions = currentWorkflow?.definition?.actions || {};

  // Check for added or modified actions
  for (const [actionName, proposedAction] of Object.entries(proposedActions)) {
    const currentAction = currentActions[actionName];
    if (!currentAction) {
      // New action - check if it's in scope
      if (!allowedSet.has(actionName)) {
        outOfScopeChanges.push({ type: 'action', name: actionName, changeType: 'added' });
      }
    } else if (!isEqual(proposedAction, currentAction)) {
      // Modified action - check if it's in scope
      // Ignore metadata-only changes (positions, etc.)
      const proposedWithoutMeta = { ...proposedAction as Record<string, unknown> };
      const currentWithoutMeta = { ...currentAction as Record<string, unknown> };
      delete proposedWithoutMeta.metadata;
      delete currentWithoutMeta.metadata;
      
      if (!isEqual(proposedWithoutMeta, currentWithoutMeta) && !allowedSet.has(actionName)) {
        outOfScopeChanges.push({ type: 'action', name: actionName, changeType: 'modified' });
      }
    }
  }

  // Check for removed actions (not in proposed but in current)
  for (const actionName of Object.keys(currentActions)) {
    if (!(actionName in proposedActions) && !allowedSet.has(actionName)) {
      outOfScopeChanges.push({ type: 'action', name: actionName, changeType: 'removed' });
    }
  }

  // Check proposed triggers
  const proposedTriggers = proposedWorkflow.definition?.triggers || {};
  const currentTriggers = currentWorkflow?.definition?.triggers || {};

  // Check for added or modified triggers
  for (const [triggerName, proposedTrigger] of Object.entries(proposedTriggers)) {
    const currentTrigger = currentTriggers[triggerName];
    if (!currentTrigger) {
      // New trigger - check if it's in scope
      if (!allowedSet.has(triggerName)) {
        outOfScopeChanges.push({ type: 'trigger', name: triggerName, changeType: 'added' });
      }
    } else if (!isEqual(proposedTrigger, currentTrigger)) {
      // Modified trigger - check if it's in scope
      // Ignore metadata-only changes (positions, etc.)
      const proposedWithoutMeta = { ...proposedTrigger as Record<string, unknown> };
      const currentWithoutMeta = { ...currentTrigger as Record<string, unknown> };
      delete proposedWithoutMeta.metadata;
      delete currentWithoutMeta.metadata;
      
      if (!isEqual(proposedWithoutMeta, currentWithoutMeta) && !allowedSet.has(triggerName)) {
        outOfScopeChanges.push({ type: 'trigger', name: triggerName, changeType: 'modified' });
      }
    }
  }

  // Check for removed triggers (not in proposed but in current)
  for (const triggerName of Object.keys(currentTriggers)) {
    if (!(triggerName in proposedTriggers) && !allowedSet.has(triggerName)) {
      outOfScopeChanges.push({ type: 'trigger', name: triggerName, changeType: 'removed' });
    }
  }

  return {
    isValid: outOfScopeChanges.length === 0,
    outOfScopeChanges
  };
}

/**
 * Apply a node update at a specific JSON path in the workflow.
 * Works for any node type: actions, triggers, tools, nested scoped actions, etc.
 */
export function applyAtPath(jsonPath: string, nodeJson: string): boolean {
  try {
    const newValue = JSON.parse(nodeJson);
    const workflowStore = useWorkflowStore.getState();
    const workflow = workflowStore.workflow;
    if (!workflow) {
      console.error('[AG-UI] applyAtPath: No workflow loaded');
      return false;
    }

    // Validate node structure - reject if it looks like a full workflow
    // Note: 'kind' alone is NOT a workflow indicator — triggers like Request have kind: "Http"
    // Only 'definition' (with nested triggers/actions) indicates a full workflow
    if (newValue.definition) {
      console.error('[AG-UI] applyAtPath: Invalid node JSON - contains workflow-level property (definition)');
      return false;
    }

    // Validate ForEach constraints if the node being applied is or contains a ForEach
    if (jsonPath.toLowerCase().includes('actions')) {
      const wrapper: Record<string, unknown> = { actions: { _target: newValue } };
      const constraintResult = validateForEachConstraints(wrapper);
      if (!constraintResult.valid) {
        notifyForEachConstraintWarnings(constraintResult.errors);
      }
    }

    // Deep clone current workflow
    const clone = JSON.parse(JSON.stringify(workflow));

    // Preserve existing metadata (position) if not in new JSON
    const existing = getValueAtPath(clone, jsonPath) as Record<string, unknown> | undefined;
    if (existing?.metadata && !newValue.metadata) {
      newValue.metadata = existing.metadata;
    }

    // Preserve runAfter if not in new JSON (to maintain workflow order)
    if (existing?.runAfter && !newValue.runAfter) {
      newValue.runAfter = existing.runAfter;
    }

    // Set the new value at the path
    setValueAtPath(clone, jsonPath, newValue);

    // Apply the updated workflow
    const result = workflowStore.updateWorkflowFromJson(JSON.stringify(clone, null, 2));
    if (result.success) {
      console.log('[AG-UI] applyAtPath: Applied update at path:', jsonPath);
    } else {
      console.error('[AG-UI] applyAtPath: Failed to apply update:', result.error);
    }
    return result.success;
  } catch (error) {
    console.error('[AG-UI] applyAtPath: Failed:', error);
    return false;
  }
}

/**
 * Re-patch the current workflow with discovered tool definitions
 * This is called when GetActionTemplate completes AFTER workflow was already applied
 */
function repatchCurrentWorkflowWithDiscoveredTools(): void {
  const toolDefinitions = getLastDiscoveredToolDefinitions();
  if (!toolDefinitions) {
    console.log('[repatchCurrentWorkflow] No tool definitions available');
    return;
  }

  const workflowStore = useWorkflowStore.getState();
  const currentWorkflow = workflowStore.workflow;
  if (!currentWorkflow) {
    console.log('[repatchCurrentWorkflow] No workflow loaded');
    return;
  }

  // Check if workflow has Agent actions with tools
  const actions = currentWorkflow.definition?.actions;
  if (!actions) {
    return;
  }

  // Check if any Agent action has Http tools
  let hasHttpInAgentTools = false;
  for (const action of Object.values(actions)) {
    const typedAction = action as { type?: string; tools?: Record<string, { actions?: Record<string, { type?: string }> }> };
    if (typedAction.type !== 'Agent') continue;
    const tools = typedAction.tools;
    if (!tools) continue;

    for (const tool of Object.values(tools)) {
      const toolActions = tool.actions;
      if (!toolActions) continue;
      for (const toolAction of Object.values(toolActions)) {
        if (toolAction.type === 'Http') {
          hasHttpInAgentTools = true;
          break;
        }
      }
      if (hasHttpInAgentTools) break;
    }
    if (hasHttpInAgentTools) break;
  }

  if (!hasHttpInAgentTools) {
    console.log('[repatchCurrentWorkflow] No Http actions in agent tools, skipping repatch');
    return;
  }

  console.log('[repatchCurrentWorkflow] Found Http actions in agent tools, attempting repatch');

  // Patch the workflow
  const { patched, fixedTools } = patchHttpActionsInAgentTools(currentWorkflow as Record<string, unknown>);

  if (fixedTools.length > 0) {
    console.log(`[repatchCurrentWorkflow] Patched ${fixedTools.length} tools:`, fixedTools);

    // Apply the patched workflow
    const result = workflowStore.updateWorkflowFromJson(JSON.stringify(patched, null, 2));
    if (result.success) {
      console.log('[repatchCurrentWorkflow] Successfully applied patched workflow');
    } else {
      console.error('[repatchCurrentWorkflow] Failed to apply patched workflow:', result.error);
    }
  }
}

// Set up the callback for when tool definitions become ready
setOnToolDefinitionsReadyCallback(repatchCurrentWorkflowWithDiscoveredTools);
